import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
  ArrowLeft,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  MessageSquare,
  Stethoscope,
  Plus,
  Lock,
  LockOpen,
  AlertTriangle,
  Info,
  Zap,
  Eraser,
  FlaskConical,
  Star,
  Puzzle,
  Ban,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth, isDevMode } from "@/lib/auth";
import { ImpersonationReasonPicker } from "@/components/shared/impersonation-reason-picker";
import { DoctorMessagesPanel } from "@/components/doctor-messages/user-threads-panel";
import {
  useCrmUsersByIds,
  useUserNeedingAction,
  useBiometrics,
  useReferenceRanges,
  useBiometricCategories,
  useCategories,
  useQuestions,
  useQuestionCategories,
  useQuestionAlternatives,
  useQuestionNumericRules,
  useBiometricPrices,
  USER_FEATURE_FLAGS,
  useFeatureFlagsByUser,
  useUpdateFeatureFlags,
  useCreateFeatureFlags,
  useAssessmentVariantMetadataList,
  useOverrideLabVisitStatus,
  usePatientImpersonators,
  useLabVisitRequisitionBiometrics,
  useLabVisitRequisitions,
  useSyncLabVisitMarkers,
  useMockLabResults,
  useGenerateAssessments,
  useLabs,
} from "@/hooks/api";
import {
  useLabVisitHelfoPartitioning,
  useHelfoClinicalInfoEditor,
} from "@/hooks/useLabVisitHelfo";
import {
  LabVisitsService,
  SurveysService,
  AssessmentsService,
  AccountsService,
} from "@/lib/api_client";
import type {
  LabVisit,
  LabVisitRequisition,
  LabVisitUpdate,
  QuestionRespons,
  BiometricResult,
  Biometric,
  BiometricReferenceRange,
  Question,
  QuestionAlternative,
  QuestionNumericRule,
  Assessment,
  AssessmentCreate,
  AssessmentUpdate,
  AssessmentVariant,
  BloodTestPackage,
  BiometricPrice,
  AssessmentEmphasizedBiometric,
  AssessmentEmphasizedBiometricUpdate,
  AssessmentEmphasizedBiometricCreate,
  AssessmentRecommendation,
  AssessmentRecommendationUpdate,
  AssessmentRecommendationCreate,
  InternalUserComment,
  InternalUserCommentCreate,
  JournalNote,
  JournalNoteCreate,
  InternalNote,
  InternalNoteCreate,
  UserProfile,
} from "@/lib/api_client";
import {
  sortLabVisitsByEffectiveDate,
  getSurveyBioIds,
  getDefaultBioIds,
  getRangesForBiometric,
  buildFurstIdGroups,
  expandToFurstGroups,
} from "@/lib/utils/bloodTestSuggestion";
import { resolveVisitLab, buildLabsById } from "@/lib/utils/resolveVisitLab";
import { visitFlowLabel } from "@/lib/utils/b2bFlowLabel";
import { buildFurstPriceMap, sumFurstCost } from "@/lib/utils/furstCost";
import { EtterrekvireringModal } from "@/components/shared/etterrekvirering-modal";
import { getAuthHeaders, impersonateHeaders } from "@/lib/api";
import { cn } from "@/lib/cn";
import { getCurrentAuthUserId } from "@/lib/current-user";
import { fetchAll } from "@/hooks/api/fetchAll";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MaskedText } from "@/components/masked-text";
import {
  AdvancedParamsDialog,
  type AdvancedParams,
} from "@/components/assessment/AdvancedParamsDialog";
import {
  GenerateAllDialog,
  type GenerateAllParams,
} from "@/components/assessment/GenerateAllDialog";
import { BrowseAsUserButton } from "@/components/browse-as-user";
import { CreateLabVisitButton } from "@/components/create-lab-visit-button";
import { ConsultationPanel } from "@/components/anamnesis/ConsultationPanel";
import { AssessmentAuditButtons } from "@/components/assessment/AssessmentAuditButtons";
import { WearableDataCard } from "@/components/assessment/WearableDataCard";
import { useWearableDeltas, useWearableCurrent } from "@/hooks/api";
import { useConsultationNotes } from "@/hooks/api/useConsultationNotes";

// ── Status helpers ──────────────────────────────────────────

const STATUS_ORDER = [
  "paid",
  "bloodTestRequested",
  "pendingRequisition",
  "requisitioned",
  "awaitingResults",
  "allBiometricResultsReceived",
  "assessmentCompleted",
] as const;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}t siden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d siden`;
  return `${Math.floor(days / 30)}mnd siden`;
}

function getStatusText(status: string | null | undefined): string {
  switch (status) {
    case "paid":
      return "Betalt";
    case "bloodTestRequested":
      return "Trenger biomarkørforslag";
    case "pendingRequisition":
      return "Venter på rekvisisjon";
    case "requisitioned":
      return "Rekvirert";
    case "requestRejected":
      return "Forespørsel avvist";
    case "awaitingResults":
      return "Venter på resultater";
    case "allBiometricResultsReceived":
      return "Trenger analyse og anbefaling";
    case "assessmentCompleted":
      return "Analyse fullført";
    default:
      return status ?? "Ukjent";
  }
}

function getStatusStep(status: string | null | undefined): number {
  const idx = STATUS_ORDER.indexOf(status as (typeof STATUS_ORDER)[number]);
  return idx >= 0 ? idx : 0;
}

function shortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${d.getFullYear().toString().slice(2)}`;
}

function ageFromBirthdate(birthDate: string | null | undefined): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  )
    age--;
  return age;
}

// ── Score helpers ───────────────────────────────────────────

function getScore(
  value: number,
  biometricId: string,
  gender: string | null | undefined,
  age: number | null,
  ranges: BiometricReferenceRange[],
): "optimal" | "normal" | "outside" | null {
  const matching = ranges.filter((r) => {
    if (r.biometricId !== biometricId) return false;
    if (r.gender && gender && r.gender !== gender) return false;
    if (age != null) {
      if (Number(r.minAge) > age || Number(r.maxAge) < age) return false;
    }
    return true;
  });
  if (matching.length === 0) return null;

  const optMin = matching.find((r) => r.referenceType === "optimal_min");
  const optMax = matching.find((r) => r.referenceType === "optimal_max");
  const normMin = matching.find((r) => r.referenceType === "normal_min");
  const normMax = matching.find((r) => r.referenceType === "normal_max");

  if (
    optMin &&
    optMax &&
    value >= Number(optMin.value) &&
    value <= Number(optMax.value)
  )
    return "optimal";
  if (
    normMin &&
    normMax &&
    value >= Number(normMin.value) &&
    value <= Number(normMax.value)
  )
    return "normal";
  if (normMin && normMax) return "outside";
  // If only optimal ranges exist
  if (optMin && optMax)
    return value >= Number(optMin.value) && value <= Number(optMax.value)
      ? "optimal"
      : "outside";
  return null;
}

// Colors from frontend/lib/colors.ts
const SCORE_COLORS = {
  optimal: "#00C49A",
  normal: "#C9E9BD",
  outside: "#F18142",
} as const;

function scoreStyle(
  score: "optimal" | "normal" | "outside" | null,
): React.CSSProperties {
  if (!score) return {};
  return {
    backgroundColor: `${SCORE_COLORS[score]}22`,
    color: SCORE_COLORS[score],
    fontWeight:
      score === "outside" ? 600 : score === "optimal" ? 500 : undefined,
  };
}

// ── Data hooks (impersonate token) ──────────────────────────

function useImpersonatedLabVisits(
  token: string | null,
  userId: string | undefined,
) {
  return useQuery({
    queryKey: ["impersonated-lab-visits", userId],
    queryFn: () =>
      fetchAll<LabVisit>((opts) =>
        LabVisitsService.getApiLabVisits({
          ...opts,
          headers: impersonateHeaders(token!, opts.headers),
        }),
      ),
    enabled: !!token && !!userId,
  });
}

function useImpersonatedBiometricResults(
  token: string | null,
  userId: string | undefined,
) {
  return useQuery({
    queryKey: ["impersonated-biometric-results", userId],
    queryFn: () =>
      fetchAll<BiometricResult>((opts) =>
        LabVisitsService.getApiBiometricResults({
          ...opts,
          headers: impersonateHeaders(token!, opts.headers),
        }),
      ),
    enabled: !!token && !!userId,
  });
}

function useImpersonatedQuestionResponses(
  token: string | null,
  userId: string | undefined,
) {
  return useQuery({
    queryKey: ["impersonated-question-responses", userId],
    queryFn: () =>
      fetchAll<QuestionRespons>((opts) =>
        SurveysService.getApiQuestionResponses({
          ...opts,
          headers: impersonateHeaders(token!, opts.headers),
        }),
      ),
    enabled: !!token && !!userId,
  });
}

function useImpersonatedUserProfile(
  token: string | null,
  userId: string | undefined,
) {
  return useQuery({
    queryKey: ["impersonated-user-profile", userId],
    queryFn: async () => {
      const res = await AccountsService.getApiUserProfiles({
        headers: impersonateHeaders(token!),
        query: { limit: 1, offset: 0, "filter[authUserId]": userId! },
      });
      if (res.error !== undefined)
        throw new Error("Failed to fetch user profile");
      const profiles = res.data as unknown as UserProfile[];
      if (!profiles?.length) throw new Error("User profile not found");
      return profiles[0];
    },
    enabled: !!token && !!userId,
  });
}

function useUpdateLabVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: LabVisitUpdate }) => {
      const res = await LabVisitsService.putApiLabVisitsById({
        path: { id },
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as LabVisit;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["impersonated-lab-visits"] });
      qc.invalidateQueries({ queryKey: ["users-needing-action"] });
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
    },
  });
}

function useAssessmentVariants() {
  return useQuery({
    queryKey: ["assessment-variants"],
    queryFn: () =>
      fetchAll<AssessmentVariant>(AssessmentsService.getApiAssessmentVariants),
    staleTime: 10 * 60_000,
  });
}

// Assessment content is patient health data: the API answers only an
// impersonation token (staffRead/staffWrite "none" in the backend's
// assessments/routes.ts), and that token is also what names the doctor in
// reviewedBy. These hooks mount only under this page's impersonation gate, so
// the ambient token is the one for the patient whose lab visit is on screen.
function useImpersonatedHeaders() {
  const { impersonateToken } = useAuth();
  return {
    token: impersonateToken,
    headers: (base?: Record<string, string>) => {
      if (!impersonateToken) throw new Error("Ikke impersonert");
      return impersonateHeaders(impersonateToken, base);
    },
  };
}

function useAssessmentsByVisit(labVisitId: string | null) {
  const { token, headers } = useImpersonatedHeaders();
  return useQuery({
    queryKey: ["assessments", labVisitId],
    queryFn: () =>
      fetchAll<Assessment>((opts) =>
        AssessmentsService.getApiAssessments({
          ...opts,
          query: { ...opts?.query, "filter[labVisitId]": labVisitId! },
          headers: headers(opts.headers),
        }),
      ),
    enabled: !!token && !!labVisitId,
  });
}

function useCreateAssessment() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async (data: AssessmentCreate) => {
      const res = await AssessmentsService.postApiAssessments({
        body: data,
        headers: headers(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Assessment;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assessments", vars.labVisitId] });
    },
  });
}

function useUpdateAssessment() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      labVisitId,
    }: {
      id: string;
      data: AssessmentUpdate;
      labVisitId: string;
    }) => {
      const res = await AssessmentsService.putApiAssessmentsById({
        path: { id },
        body: data,
        headers: headers(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Assessment;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assessments", vars.labVisitId] });
    },
  });
}

function useDeleteAssessment() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({
      id,
      labVisitId,
    }: {
      id: string;
      labVisitId: string;
    }) => {
      await AssessmentsService.deleteApiAssessmentsById({
        path: { id },
        headers: headers(),
      });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assessments", vars.labVisitId] });
    },
  });
}

// ── Emphasized Biometrics hooks ──────────────────────────────

function useEmphasizedBiometrics(assessmentId: string | null) {
  const { token, headers } = useImpersonatedHeaders();
  return useQuery({
    queryKey: ["emphasized-biometrics", assessmentId],
    queryFn: () =>
      fetchAll<AssessmentEmphasizedBiometric>((opts) =>
        AssessmentsService.getApiAssessmentEmphasizedBiometrics({
          ...opts,
          query: { ...opts?.query, "filter[assessmentId]": assessmentId! },
          headers: headers(opts.headers),
        }),
      ),
    enabled: !!token && !!assessmentId,
  });
}

function useUpdateEmphasizedBiometric() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      assessmentId,
    }: {
      id: string;
      data: AssessmentEmphasizedBiometricUpdate;
      assessmentId: string;
    }) => {
      const res =
        await AssessmentsService.putApiAssessmentEmphasizedBiometricsById({
          path: { id },
          body: data,
          headers: headers(),
        });
      if (!res.data) throw new Error("Failed");
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["emphasized-biometrics", vars.assessmentId],
      });
    },
  });
}

function useCreateEmphasizedBiometric() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({
      data,
      assessmentId,
    }: {
      data: AssessmentEmphasizedBiometricCreate;
      assessmentId: string;
    }) => {
      const res =
        await AssessmentsService.postApiAssessmentEmphasizedBiometrics({
          body: data,
          headers: headers(),
        });
      if (!res.data) throw new Error("Failed");
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["emphasized-biometrics", vars.assessmentId],
      });
    },
  });
}

function useDeleteEmphasizedBiometric() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({ id }: { id: string; assessmentId: string }) => {
      await AssessmentsService.deleteApiAssessmentEmphasizedBiometricsById({
        path: { id },
        headers: headers(),
      });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["emphasized-biometrics", vars.assessmentId],
      });
    },
  });
}

// ── Recommendations hooks ────────────────────────────────────

function useRecommendations(assessmentId: string | null) {
  const { token, headers } = useImpersonatedHeaders();
  return useQuery({
    queryKey: ["recommendations", assessmentId],
    queryFn: () =>
      fetchAll<AssessmentRecommendation>((opts) =>
        AssessmentsService.getApiAssessmentRecommendations({
          ...opts,
          query: { ...opts?.query, "filter[assessmentId]": assessmentId! },
          headers: headers(opts.headers),
        }),
      ),
    enabled: !!token && !!assessmentId,
  });
}

function useUpdateRecommendation() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      assessmentId,
    }: {
      id: string;
      data: AssessmentRecommendationUpdate;
      assessmentId: string;
    }) => {
      const res = await AssessmentsService.putApiAssessmentRecommendationsById({
        path: { id },
        body: data,
        headers: headers(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["recommendations", vars.assessmentId],
      });
    },
  });
}

function useCreateRecommendation() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({
      data,
      assessmentId,
    }: {
      data: AssessmentRecommendationCreate;
      assessmentId: string;
    }) => {
      const res = await AssessmentsService.postApiAssessmentRecommendations({
        body: data,
        headers: headers(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["recommendations", vars.assessmentId],
      });
    },
  });
}

function useDeleteRecommendation() {
  const qc = useQueryClient();
  const { headers } = useImpersonatedHeaders();
  return useMutation({
    mutationFn: async ({ id }: { id: string; assessmentId: string }) => {
      await AssessmentsService.deleteApiAssessmentRecommendationsById({
        path: { id },
        headers: headers(),
      });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["recommendations", vars.assessmentId],
      });
    },
  });
}

const VARIANT_ORDER: Record<string, number> = {
  hearthealth: 1,
  stress: 2,
  fitness: 3,
  bodyComposition: 4,
  sleep: 5,
  other: 6,
  emphasizedBiometrics: 7,
  reccomendations: 8,
  summary: 9,
};

// Freetext questions about what the patient wants the doctor to look at. They
// live in different categories (Onboarding / Annet) but doctors read them first,
// so they're hoisted out to the top of the survey in the order listed here.
const PINNED_QUESTION_IDS = [
  "f47895de-75ab-4da4-bf4e-64fdc30e246a",
  "1798b0cf-290d-41aa-836c-3078b431f8ad",
];

// ── Main component ──────────────────────────────────────────

export function AnamnesisUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    stopImpersonating,
    impersonate,
    impersonateToken,
    impersonatingUserId,
    hasRole,
  } = useAuth();

  // Admin-scoped data
  const { data: crmUsers } = useCrmUsersByIds(userId ? [userId] : []);
  const { data: userActions = [] } = useUserNeedingAction(userId);
  const { data: allBiometrics } = useBiometrics();
  const { data: refRanges } = useReferenceRanges();
  const { data: bioCats } = useBiometricCategories();
  const { data: categories } = useCategories();
  const { data: questions } = useQuestions();
  const { data: questionCategories } = useQuestionCategories();
  const { data: questionAlternatives } = useQuestionAlternatives();
  const { data: questionNumericRules } = useQuestionNumericRules();
  const { data: biometricPrices } = useBiometricPrices();

  // Arriving from a list that already collected a reason carries a live token,
  // so the reason dialog only appears on a direct landing: a deep link, a
  // refresh, or a dashboard mail.
  //
  // Only a token minted for *this* patient may reach the data hooks. The server
  // scopes every response by the token's subject, and the hooks key on userId
  // alone, so fetching B's keys with A's token caches A's journal under B and
  // re-impersonating does not refetch it — the key never changed.
  const activeToken =
    impersonatingUserId === userId ? impersonateToken : null;
  const needsImpersonationReason = !!userId && !activeToken;
  const [impersonationReason, setImpersonationReason] = useState("");
  const [impersonationPending, setImpersonationPending] = useState(false);

  const confirmImpersonation = async (selectedReason: string) => {
    if (!userId || impersonationPending) return;
    const reason = selectedReason.trim();
    if (!reason) return;
    setImpersonationPending(true);
    try {
      await impersonate(userId, reason);
      setImpersonationReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunne ikke impersonere bruker");
      navigate("/anamnesis");
    } finally {
      setImpersonationPending(false);
    }
  };

  // The admin-token reads below are patient data too, so they wait for the
  // reason like the impersonated ones. Otherwise a deep link that is cancelled
  // would still have fetched journal notes with no grunnlag logged.
  const patientId = activeToken ? userId : undefined;

  // The impersonation token, and the audit row written when it was minted, is
  // the record of *this* view of the journal — so it must not outlive the view.
  // Without this, leaving by anything other than the back button (a sidebar
  // link, browser back) left the token alive, and re-opening the same patient
  // reused it and produced no new audit row. userId is in the deps because the
  // route reuses this component across :userId, so an unmount alone would miss
  // patient A → patient B → back to A.
  useEffect(() => stopImpersonating, [stopImpersonating, userId]);

  const { data: currentWearable, isSuccess: currentWearableLoaded } =
    useWearableCurrent(activeToken, patientId);

  // User-scoped data (impersonate)
  const { data: labVisits, isLoading: lvLoading } = useImpersonatedLabVisits(
    activeToken,
    userId,
  );
  const { data: bioResults, isLoading: brLoading } =
    useImpersonatedBiometricResults(activeToken, userId);
  const { data: questionResponses, isLoading: qrLoading } =
    useImpersonatedQuestionResponses(activeToken, userId);
  const { data: userProfile } = useImpersonatedUserProfile(
    activeToken,
    userId,
  );

  const { data: patientImpersonators } = usePatientImpersonators(patientId);

  // Toast when another doctor opens this patient while we're viewing
  const seenImpersonatorsRef = useRef<Map<string, string> | null>(null); // adminId -> lastViewedAt
  useEffect(() => {
    if (!patientImpersonators) return;

    const currentAdminId = getCurrentAuthUserId();

    if (seenImpersonatorsRef.current === null) {
      // First load — establish baseline silently
      seenImpersonatorsRef.current = new Map(
        patientImpersonators.map((imp) => [imp.adminId, imp.lastViewedAt]),
      );
      return;
    }

    for (const imp of patientImpersonators) {
      if (imp.adminId === currentAdminId) continue;
      const prev = seenImpersonatorsRef.current.get(imp.adminId);
      if (!prev || imp.lastViewedAt > prev) {
        toast.info(`${imp.adminName} åpnet denne pasienten`);
        seenImpersonatorsRef.current.set(imp.adminId, imp.lastViewedAt);
      }
    }
  }, [patientImpersonators]);

  const updateLabVisit = useUpdateLabVisit();
  const overrideStatus = useOverrideLabVisitStatus();
  const queryClient = useQueryClient();
  const { data: assessmentVariants } = useAssessmentVariants();

  // Feature flags
  const { data: userFeatureFlag } = useFeatureFlagsByUser(userId);
  const updateFeatureFlags = useUpdateFeatureFlags();
  const createFeatureFlags = useCreateFeatureFlags();

  // Internal user comments
  const { data: internalComments, refetch: refetchComments } = useQuery({
    queryKey: ["internal-user-comments", userId],
    queryFn: () =>
      fetchAll<InternalUserComment>((opts) =>
        AccountsService.getApiInternalUserComments({
          ...opts,
          query: { ...opts?.query, "filter[authUserId]": userId! },
          headers: getAuthHeaders(),
        }),
      ),
    enabled: !!patientId,
  });
  const createCommentMutation = useMutation({
    mutationFn: async (data: InternalUserCommentCreate) => {
      const res = await AccountsService.postApiInternalUserComments({
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as InternalUserComment;
    },
    onSuccess: () => refetchComments(),
  });
  const deleteCommentMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await AccountsService.deleteApiInternalUserCommentsById({
        path: { id },
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error("Failed");
    },
    // Prefix match: also refreshes the anamnesis list's comment cache.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["internal-user-comments"] }),
  });
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState<
    string | null
  >(null);

  // Journal notes are append-only, so there is no edit or delete affordance.
  const { data: journalNotes, refetch: refetchJournalNotes } = useQuery({
    queryKey: ["journal-notes", userId],
    queryFn: () =>
      fetchAll<JournalNote>((opts) =>
        AccountsService.getApiJournalNotes({
          ...opts,
          // Oldest first, and explicitly: a correction is a new note referring
          // to the one it corrects, so it has to render below it. Without an
          // order the rows come back in whatever order Postgres has them.
          query: {
            ...opts?.query,
            "filter[authUserId]": userId!,
            order: "createdAt.asc",
          },
          headers: getAuthHeaders(),
        }),
      ),
    enabled: !!patientId,
  });
  const createJournalNoteMutation = useMutation({
    mutationFn: async (data: JournalNoteCreate) => {
      const res = await AccountsService.postApiJournalNotes({
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as JournalNote;
    },
    onSuccess: () => refetchJournalNotes(),
  });

  // Internal lab visit notes (fetched per selected visit)
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  // We need selectedVisit computed before the notes query, but it depends on labVisits.
  // The notes query is defined below after selectedVisit is computed.

  const [surveyOpen, setSurveyOpen] = useState(true);
  const [expandedBioId, setExpandedBioId] = useState<string | null>(null);
  const [showSelfReported, setShowSelfReported] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [journalOpen, setJournalOpen] = useState(false);
  const [newJournalNote, setNewJournalNote] = useState("");
  const [newComment, setNewComment] = useState("");
  const [newVisitNote, setNewVisitNote] = useState("");
  const [visitNotesOpen, setVisitNotesOpen] = useState(false);
  const [stickyTarget, setStickyTarget] = useState<HTMLDivElement | null>(null);

  // Clear draft note when switching visits to avoid saving to wrong visit
  useEffect(() => {
    setNewVisitNote("");
  }, [selectedVisitId]);

  const crmUser = crmUsers?.[0];
  const userAge = ageFromBirthdate(
    userProfile?.birthDate ?? crmUser?.birthDate,
  );
  const userGender = userProfile?.gender ?? crmUser?.gender;

  // Lab visits sorted newest first
  const allSortedVisits = useMemo(() => {
    if (!labVisits) return [];
    return sortLabVisitsByEffectiveDate(labVisits);
  }, [labVisits]);

  const sortedVisits = useMemo(() => {
    if (showSelfReported) return allSortedVisits;
    return allSortedVisits.filter((v) => !v.selfReported);
  }, [allSortedVisits, showSelfReported]);

  // Auto-focus the newest lab visit that needs analysis (non-self-reported first)
  const defaultVisit = useMemo(() => {
    if (!sortedVisits.length) return null;
    // Prioritize analysis-ready visits, then other actionable statuses
    const actionStatuses = [
      "allBiometricResultsReceived",
      "bloodTestRequested",
      "requisitioned",
      "pendingRequisition",
      "paid",
    ] as const;
    for (const status of actionStatuses) {
      // Prefer non-self-reported visits for the default selection
      const match = sortedVisits.find(
        (v) => v.status === status && !v.selfReported,
      );
      if (match) return match;
    }
    // Fall back to any non-self-reported visit, then any visit
    return sortedVisits.find((v) => !v.selfReported) ?? sortedVisits[0];
  }, [sortedVisits]);

  const selectedVisit =
    sortedVisits.find((v) => v.id === selectedVisitId) ?? defaultVisit ?? null;

  // Internal notes for the selected lab visit
  const selectedLabVisitId = selectedVisit?.id;
  const { data: visitNotes } = useQuery({
    queryKey: ["internal-notes", selectedLabVisitId],
    queryFn: () =>
      fetchAll<InternalNote>((opts) =>
        LabVisitsService.getApiInternalNotes({
          ...opts,
          query: { ...opts?.query, "filter[labVisitId]": selectedLabVisitId! },
          headers: getAuthHeaders(),
        }),
      ),
    enabled: !!selectedLabVisitId,
  });
  const createVisitNoteMutation = useMutation({
    mutationFn: async (data: InternalNoteCreate) => {
      const res = await LabVisitsService.postApiInternalNotes({
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data)
        throw new Error("Failed to create internal note for lab visit");
      return res.data as unknown as InternalNote;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-notes"] });
    },
  });

  // Auto-expand internt labnotat when there are notes, collapse when empty
  useEffect(() => {
    setVisitNotesOpen((visitNotes ?? []).length > 0);
  }, [visitNotes]);

  // Health goals from question responses
  const healthGoals = useMemo(() => {
    if (!questionResponses || !questions) return null;
    const hgQ = questions.find((q) => q.nickname === "healthgoals");
    if (!hgQ) return null;
    const responses = questionResponses
      .filter((r) => r.questionId === hgQ.id)
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
    const latest = responses[0];
    if (!latest?.response) return null;
    return latest.response.replace(/freetext:/g, "Annet: ");
  }, [questionResponses, questions]);

  // ── Survey data: group all responses by question, newest first ──
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [expandedQuestionHistory, setExpandedQuestionHistory] = useState<
    Set<string>
  >(new Set());

  const surveyCategoryData = useMemo(() => {
    if (
      !questionResponses ||
      questionResponses.length === 0 ||
      !questions ||
      !questionCategories
    )
      return [];

    const qMap = new Map(questions.map((q) => [q.id, q]));
    const qCatMap = new Map(questions.map((q) => [q.id, q.categoryId]));

    // Group all responses by questionId, sorted newest first
    const byQuestion = new Map<string, QuestionRespons[]>();
    for (const qr of questionResponses) {
      if (!qr.questionId) continue;
      const list = byQuestion.get(qr.questionId) ?? [];
      list.push(qr);
      byQuestion.set(qr.questionId, list);
    }
    // Sort each group newest first and deduplicate identical consecutive answers.
    // updatedAt anchors on the latest edit so same-day corrections (patient
    // upserts, doctor backfills) bubble to the top of the history list.
    for (const [qId, list] of byQuestion) {
      list.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      // Deduplicate: keep only entries where response differs from the next (older) one, plus always keep the oldest
      const deduped: QuestionRespons[] = [];
      for (let i = 0; i < list.length; i++) {
        if (
          i === list.length - 1 ||
          list[i].response !== list[i + 1].response
        ) {
          deduped.push(list[i]);
        }
      }
      byQuestion.set(qId, deduped);
    }

    // Group by category
    const grouped = new Map<
      string,
      {
        questionId: string;
        newest: QuestionRespons;
        history: QuestionRespons[];
      }[]
    >();
    for (const [qId, responses] of byQuestion) {
      const catId = qCatMap.get(qId) ?? "uncategorized";
      const list = grouped.get(catId) ?? [];
      list.push({
        questionId: qId,
        newest: responses[0],
        history: responses.slice(1),
      });
      grouped.set(catId, list);
    }

    // Sort within each category by sequenceNumber
    for (const [, list] of grouped) {
      list.sort((a, b) => {
        const seqA = qMap.get(a.questionId)?.sequenceNumber ?? 999;
        const seqB = qMap.get(b.questionId)?.sequenceNumber ?? 999;
        return seqA - seqB;
      });
    }

    // Pull the pinned questions out of their categories; splice returns the
    // removed entry, so this both collects and removes in one pass.
    const pinnedItems = PINNED_QUESTION_IDS.flatMap((qId) => {
      for (const list of grouped.values()) {
        const idx = list.findIndex((i) => i.questionId === qId);
        if (idx !== -1) return list.splice(idx, 1);
      }
      return [];
    });

    // Build sorted result following questionCategories order
    const result: {
      categoryId: string;
      categoryName: string;
      items: {
        questionId: string;
        newest: QuestionRespons;
        history: QuestionRespons[];
      }[];
    }[] = [];
    if (pinnedItems.length > 0) {
      result.push({
        categoryId: "pinned",
        categoryName: "Fritekst",
        items: pinnedItems,
      });
    }
    for (const cat of questionCategories) {
      const items = grouped.get(cat.id);
      if (items && items.length > 0) {
        result.push({ categoryId: cat.id, categoryName: cat.name, items });
      }
    }
    const uncategorized = grouped.get("uncategorized");
    if (uncategorized && uncategorized.length > 0) {
      result.push({
        categoryId: "uncategorized",
        categoryName: "Annet",
        items: uncategorized,
      });
    }

    return result;
  }, [questionResponses, questions, questionCategories]);

  // ── Biometrics table data ─────────────────────────────────

  // Group results by labVisitId
  const resultsByVisit = useMemo(() => {
    const map = new Map<string, BiometricResult[]>();
    for (const r of bioResults ?? []) {
      const list = map.get(r.labVisitId) ?? [];
      list.push(r);
      map.set(r.labVisitId, list);
    }
    return map;
  }, [bioResults]);

  // Visits that have results, sorted by visitDate desc (newest left, oldest right)
  const visitsWithResults = useMemo(() => {
    return sortedVisits
      .filter((v) => (resultsByVisit.get(v.id)?.length ?? 0) > 0)
      .sort(
        (a, b) =>
          new Date(b.visitDate ?? b.createdAt).getTime() -
          new Date(a.visitDate ?? a.createdAt).getTime(),
      );
  }, [sortedVisits, resultsByVisit]);

  // All unique biometric IDs across results
  const biometricIdsInResults = useMemo(() => {
    const ids = new Set<string>();
    for (const results of resultsByVisit.values()) {
      for (const r of results) ids.add(r.biometricId);
    }
    return ids;
  }, [resultsByVisit]);

  // Build rows: biometrics grouped by category
  const bioRows = useMemo(() => {
    if (!allBiometrics) return [];
    const rows: { bio: Biometric; categoryName: string }[] = [];
    for (const bio of allBiometrics) {
      if (!biometricIdsInResults.has(bio.id)) continue;
      const catIds = (bioCats ?? [])
        .filter((bc) => bc.biometricId === bio.id)
        .map((bc) => bc.categoryId);
      const cat = (categories ?? []).find((c) => catIds.includes(c.id));
      rows.push({ bio, categoryName: cat?.name ?? "Annet" });
    }
    rows.sort(
      (a, b) =>
        a.categoryName.localeCompare(b.categoryName, "nb") ||
        a.bio.name.localeCompare(b.bio.name, "nb"),
    );
    return rows;
  }, [allBiometrics, biometricIdsInResults, bioCats, categories]);

  // ── Workflow step actions ─────────────────────────────────

  const handleStatusChange = async (
    visitId: string,
    newStatus: LabVisitUpdate["status"],
  ) => {
    try {
      await updateLabVisit.mutateAsync({
        id: visitId,
        data: { status: newStatus },
      });
      toast.success(`Status endret til ${getStatusText(newStatus)}`);
    } catch (e) {
      // The backend's lifecycle guards say exactly what is blocking the
      // transition; showing only "Kunne ikke endre status" turns a solvable
      // step into a mystery.
      toast.error(
        e instanceof Error && e.message
          ? e.message
          : "Kunne ikke endre status",
      );
    }
  };

  const handleRevertStatus = async (
    visitId: string,
    newStatus: NonNullable<LabVisitUpdate["status"]>,
  ) => {
    try {
      await overrideStatus.mutateAsync({ id: visitId, status: newStatus });
      queryClient.invalidateQueries({ queryKey: ["impersonated-lab-visits"] });
      toast.success(`Status tilbakeført til ${getStatusText(newStatus)}`);
    } catch {
      toast.error("Kunne ikke tilbakeføre status");
    }
  };

  // No stopImpersonating() here — the unmount cleanup above already drops the
  // token when the route changes.
  const handleBack = () => navigate("/anamnesis");

  const isLoading = !activeToken || lvLoading || brLoading;

  const userName =
    (userProfile?.name ??
      [userProfile?.givenName, userProfile?.familyName]
        .filter(Boolean)
        .join(" ")) ||
    crmUser?.fullName ||
    [crmUser?.givenName, crmUser?.familyName].filter(Boolean).join(" ") ||
    "Ukjent bruker";

  // Not dismissable to the page behind it: without a token every patient query
  // is disabled, so a dismissed dialog would leave an empty shell that looks
  // broken. Cancelling goes back the same way the back button does, so a
  // session left over from a previous patient does not stay live.
  if (needsImpersonationReason) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) handleBack();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grunn for impersonering</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-admin-text-secondary">
              Oppslag i pasientjournal blir loggført med begrunnelsen du oppgir.
            </p>
            <ImpersonationReasonPicker
              key={userId}
              value={impersonationReason}
              onChange={setImpersonationReason}
              disabled={impersonationPending}
              onConfirm={confirmImpersonation}
              confirmLabel="Bekreft og åpne pasient"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={handleBack}>
                Avbryt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-[calc(100vh-6rem)] min-h-0">
      {/* ── Left: Main content (scrollable) ────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-2">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-baseline gap-3">
              <h2 className="text-xl font-semibold">
                <MaskedText value={userName} />
              </h2>
              <span className="text-sm text-admin-text-subtle">
                {userAge != null && `${userAge} år`}
                {userGender &&
                  ` · ${userGender === "male" ? "Mann" : userGender === "female" ? "Kvinne" : userGender}`}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm text-admin-text-secondary">
              {(userProfile?.email ?? crmUser?.email) && (
                <span>
                  <MaskedText
                    value={(userProfile?.email ?? crmUser?.email)!}
                    type="email"
                  />
                </span>
              )}
              {userProfile?.nin && (
                <span>
                  · <MaskedText value={userProfile.nin} type="nin" />
                </span>
              )}
              {crmUser?.labName && (
                <span>
                  · {crmUser.labName}
                  {crmUser.labCity ? `, ${crmUser.labCity}` : ""}
                </span>
              )}
            </div>
            {healthGoals && (
              <div className="flex flex-wrap gap-1 mt-1">
                {healthGoals.split(";").map((g) => (
                  <span
                    key={g.trim()}
                    className="text-xs bg-admin-primary/20 text-admin-text-primary rounded-full px-2 py-0.5"
                  >
                    {g.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Action badges */}
          {userActions.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {userActions.map((a, i) => (
                <Badge
                  key={`${a.id}-${i}`}
                  variant={
                    a.status === "allBiometricResultsReceived" ||
                    a.status === "paid"
                      ? "info"
                      : "default"
                  }
                >
                  {getStatusText(a.status)}
                </Badge>
              ))}
            </div>
          )}
          {userId && (
            <div className="flex gap-1">
              <CreateLabVisitButton userId={userId} userName={userName} />
              <BrowseAsUserButton userId={userId} />
            </div>
          )}
        </div>

        {/* Sett av */}
        {patientImpersonators && patientImpersonators.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-admin-text-subtle uppercase tracking-wide">
              Sett av
            </span>
            {patientImpersonators
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.lastViewedAt).getTime() -
                  new Date(a.lastViewedAt).getTime(),
              )
              .map((imp) => (
                <span
                  key={imp.adminId}
                  className="text-xs bg-admin-card border border-admin-card-border rounded-full px-2.5 py-0.5 text-admin-text-secondary"
                  title={imp.lastViewedAt}
                >
                  {imp.adminName} · {timeAgo(imp.lastViewedAt)}
                </span>
              ))}
          </div>
        )}

        {/* Journal notes — clinical, append-only */}
        <Card className="border-l-4 border-l-admin-sky-500/70">
          <CardContent className="p-0">
            <button
              className="w-full flex items-center justify-between p-3 hover:bg-admin-card/50"
              onClick={() => setJournalOpen(!journalOpen)}
            >
              <span className="text-sm font-semibold text-admin-sky-400 flex items-center gap-2">
                <Stethoscope className="h-4 w-4" />
                Journalnotat{" "}
                {journalNotes && `(${journalNotes.length})`}
              </span>
              {journalOpen ? (
                <ChevronDown className="h-4 w-4 text-admin-text-subtle" />
              ) : (
                <ChevronRight className="h-4 w-4 text-admin-text-subtle" />
              )}
            </button>
            {journalOpen && (
              <div className="border-t border-admin-card-border p-3 space-y-3">
                <p className="text-xs text-admin-text-subtle">
                  Oppfølging av kliniske funn. Journalinnhold — kan ikke endres
                  eller slettes etter lagring; en retting skrives som nytt notat.
                  Bruk «Interne kommentarer» under til alt som gjelder
                  kundeforholdet.
                </p>
                {(journalNotes ?? []).map((n) => (
                  <div
                    key={n.id}
                    className="border-l-2 border-l-admin-sky-500/50 bg-admin-sky-500/5 rounded-r p-3"
                  >
                    <p className="text-sm text-admin-text-primary whitespace-pre-wrap leading-relaxed">
                      {n.note}
                    </p>
                    <p className="text-xs text-admin-text-subtle mt-2">
                      {n.authorId} · {shortDate(n.createdAt)}
                    </p>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Textarea
                    value={newJournalNote}
                    onChange={(e) => setNewJournalNote(e.target.value)}
                    placeholder="Tidspunkt, hvem som kontaktet, kanal, funn, hva pasienten ble rådet til, om kontakt ble bekreftet, neste steg..."
                    className="min-h-[60px] text-sm flex-1"
                  />
                  <Button
                    size="sm"
                    disabled={
                      !newJournalNote.trim() ||
                      createJournalNoteMutation.isPending
                    }
                    onClick={async () => {
                      if (!userId || !newJournalNote.trim()) return;
                      try {
                        await createJournalNoteMutation.mutateAsync({
                          authUserId: userId,
                          note: newJournalNote.trim(),
                        });
                        setNewJournalNote("");
                        toast.success("Journalnotat lagret");
                      } catch {
                        toast.error("Kunne ikke lagre journalnotat");
                      }
                    }}
                  >
                    Lagre
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Internal Comments + Feature Flags */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-l-4 border-l-admin-warn-500/70">
            <CardContent className="p-0">
              <button
                className="w-full flex items-center justify-between p-3 hover:bg-admin-card/50"
                onClick={() => setCommentsOpen(!commentsOpen)}
              >
                <span className="text-sm font-semibold text-admin-warn-400 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Interne kommentarer{" "}
                  {internalComments && `(${internalComments.length})`}
                </span>
                {commentsOpen ? (
                  <ChevronDown className="h-4 w-4 text-admin-text-subtle" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-admin-text-subtle" />
                )}
              </button>
              {commentsOpen && (
                <div className="border-t border-admin-card-border p-3 space-y-3">
                  {(internalComments ?? []).map((c) => (
                    <div
                      key={c.id}
                      className="border-l-2 border-l-admin-warn-500/50 bg-admin-warn-500/5 rounded-r p-3"
                    >
                      <p className="text-sm text-admin-text-primary whitespace-pre-wrap leading-relaxed">
                        {c.comment}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-admin-text-subtle">
                          {shortDate(c.createdAt)}
                        </p>
                        {confirmDeleteCommentId === c.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-admin-text-subtle">
                              Slett?
                            </span>
                            <button
                              className="text-xs text-admin-danger-400 hover:text-admin-danger-300"
                              disabled={deleteCommentMutation.isPending}
                              onClick={async () => {
                                try {
                                  await deleteCommentMutation.mutateAsync(c.id);
                                  setConfirmDeleteCommentId(null);
                                  toast.success("Kommentar slettet");
                                } catch {
                                  toast.error("Kunne ikke slette kommentar");
                                }
                              }}
                            >
                              Ja
                            </button>
                            <button
                              className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                              onClick={() => setConfirmDeleteCommentId(null)}
                            >
                              Nei
                            </button>
                          </div>
                        ) : (
                          <button
                            className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                            onClick={() => setConfirmDeleteCommentId(c.id)}
                          >
                            Slett
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Legg til kommentar..."
                      className="min-h-[40px] text-sm flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={
                        !newComment.trim() || createCommentMutation.isPending
                      }
                      onClick={async () => {
                        if (!userId || !newComment.trim()) return;
                        try {
                          await createCommentMutation.mutateAsync({
                            authUserId: userId,
                            comment: newComment.trim(),
                          });
                          setNewComment("");
                          toast.success("Kommentar lagt til");
                        } catch {
                          toast.error("Kunne ikke legge til kommentar");
                        }
                      }}
                    >
                      Lagre
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <span className="text-sm font-medium text-admin-text-subtle">
                Feature flags
              </span>
              <div className="mt-2 flex flex-wrap gap-3">
                {USER_FEATURE_FLAGS.map(({ key: flag, label }) => {
                  const isActive = userFeatureFlag?.features?.[flag] ?? false;
                  return (
                    <label
                      key={flag}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={async () => {
                          const newFeatures = {
                            ...(userFeatureFlag?.features ?? {}),
                            [flag]: !isActive,
                          };
                          if (userFeatureFlag) {
                            updateFeatureFlags.mutate({
                              id: userFeatureFlag.id,
                              features: newFeatures,
                            });
                          } else if (userId) {
                            createFeatureFlags.mutate({
                              authUserId: userId,
                              features: newFeatures,
                            });
                          }
                        }}
                        className="h-4 w-4 rounded border-admin-card-border"
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Survey Responses — session-aware, one card per category */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <button
              className="flex items-center gap-2 hover:opacity-80"
              onClick={() => setSurveyOpen(!surveyOpen)}
            >
              {surveyOpen ? (
                <ChevronDown className="h-4 w-4 text-admin-text-subtle" />
              ) : (
                <ChevronRight className="h-4 w-4 text-admin-text-subtle" />
              )}
              <span className="text-sm font-medium text-admin-text-subtle">
                Spørreskjema{" "}
                {questionResponses && `(${questionResponses.length})`}
              </span>
            </button>
          </div>
          {surveyOpen &&
            (qrLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
              </div>
            ) : surveyCategoryData.length > 0 ? (
              <div className="space-y-2">
                {surveyCategoryData.map((group) => {
                  const isCollapsed = collapsedCategories.has(group.categoryId);

                  return (
                    <Card key={group.categoryId}>
                      <CardContent className="p-0">
                        <button
                          className="w-full flex items-center gap-2 py-2 px-3 hover:bg-admin-card/50 rounded-t"
                          onClick={() => {
                            const next = new Set(collapsedCategories);
                            isCollapsed
                              ? next.delete(group.categoryId)
                              : next.add(group.categoryId);
                            setCollapsedCategories(next);
                          }}
                        >
                          {isCollapsed ? (
                            <ChevronRight className="h-3.5 w-3.5 text-admin-text-subtle shrink-0" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5 text-admin-text-subtle shrink-0" />
                          )}
                          <span className="text-xs uppercase tracking-wider text-admin-text-subtle font-semibold">
                            {group.categoryName}
                          </span>
                          <span className="text-xs text-admin-text-subtle">
                            ({group.items.length})
                          </span>
                        </button>
                        {!isCollapsed && (
                          <div className="border-t border-admin-card-border divide-y divide-admin-card-border">
                            {group.items.map(
                              ({ questionId, newest, history }) => {
                                const isMultiSelect =
                                  newest.response?.includes(";");
                                const hasHistory = history.length > 0;
                                const isHistoryOpen =
                                  expandedQuestionHistory.has(questionId);

                                return (
                                  <div
                                    key={questionId}
                                    className="text-sm py-1.5 px-3"
                                  >
                                    <div className="flex gap-3">
                                      <span className="text-admin-text-secondary w-[40%] shrink-0">
                                        {newest.questionText}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2">
                                          <div className="flex-1 min-w-0">
                                            {isMultiSelect ? (
                                              <div className="flex flex-wrap gap-1">
                                                {newest.response
                                                  .split(";")
                                                  .map((v, i) => (
                                                    <span
                                                      key={i}
                                                      className="text-xs bg-admin-card-header text-admin-text-primary rounded px-1.5 py-0.5"
                                                    >
                                                      {v.trim()}
                                                    </span>
                                                  ))}
                                              </div>
                                            ) : (
                                              <span className="text-admin-text-primary font-medium">
                                                {newest.response}
                                              </span>
                                            )}
                                          </div>
                                          <span className="text-[10px] text-admin-text-subtle whitespace-nowrap shrink-0">
                                            {shortDate(newest.createdAt)}
                                          </span>
                                        </div>
                                        {newest.secondaryQuestion &&
                                          newest.secondaryQuestionResponse && (
                                            <div className="text-xs text-admin-text-subtle mt-0.5">
                                              {newest.secondaryQuestion}:{" "}
                                              <span className="text-admin-text-secondary">
                                                {
                                                  newest.secondaryQuestionResponse
                                                }
                                              </span>
                                            </div>
                                          )}
                                        {hasHistory && (
                                          <button
                                            className="text-[10px] text-admin-primary hover:underline mt-0.5 flex items-center gap-0.5"
                                            onClick={() => {
                                              const next = new Set(
                                                expandedQuestionHistory,
                                              );
                                              isHistoryOpen
                                                ? next.delete(questionId)
                                                : next.add(questionId);
                                              setExpandedQuestionHistory(next);
                                            }}
                                          >
                                            {isHistoryOpen ? (
                                              <ChevronDown className="h-2.5 w-2.5" />
                                            ) : (
                                              <ChevronRight className="h-2.5 w-2.5" />
                                            )}
                                            {history.length} tidligere svar
                                          </button>
                                        )}
                                        {hasHistory && isHistoryOpen && (
                                          <div className="mt-1 space-y-1 border-l-2 border-admin-card-border pl-2">
                                            {history.map((prev) => (
                                              <div
                                                key={prev.id}
                                                className="flex items-baseline gap-2"
                                              >
                                                <span className="text-xs text-admin-text-subtle line-through">
                                                  {prev.response}
                                                </span>
                                                <span className="text-[10px] text-admin-text-subtle whitespace-nowrap">
                                                  {shortDate(prev.createdAt)}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-admin-text-subtle">Ingen svar</p>
            ))}
        </div>

        {/* Biometrics Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-admin-card z-10">
                  <tr className="border-b border-admin-card-border">
                    <th className="text-left text-sm font-medium text-admin-text-subtle p-2 w-48 sticky left-0 bg-admin-card z-20">
                      Biomarkør
                    </th>
                    {visitsWithResults.map((v) => (
                      <th
                        key={v.id}
                        className={`text-center text-xs font-medium px-0.5 py-2 w-[44px] ${v.selfReported ? "bg-amber-100/60 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "text-admin-text-subtle"}`}
                      >
                        {shortDate(v.visitDate ?? v.createdAt)}
                        {v.selfReported && (
                          <div className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 leading-tight">
                            Egenrapp.
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bioRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visitsWithResults.length + 1}
                        className="p-4 text-center text-admin-text-subtle"
                      >
                        Ingen biomarkørresultater
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      let lastCat = "";
                      const rows: React.ReactNode[] = [];
                      for (const { bio, categoryName } of bioRows) {
                        const showCatHeader = categoryName !== lastCat;
                        lastCat = categoryName;
                        const isExpanded = expandedBioId === bio.id;
                        const ranges = getRangesForBiometric(
                          bio.id,
                          userGender,
                          userAge,
                          refRanges ?? [],
                        );
                        const hasRanges =
                          ranges.optMin ||
                          ranges.optMax ||
                          ranges.normMin ||
                          ranges.normMax;

                        rows.push(
                          <tr
                            key={bio.id}
                            className={isExpanded ? "bg-admin-card/80" : ""}
                          >
                            <td
                              className={`p-1.5 pl-2 sticky left-0 bg-admin-card z-10 ${showCatHeader ? "border-t border-admin-card-border" : ""}`}
                            >
                              {showCatHeader && (
                                <div className="text-xs uppercase tracking-wider text-admin-text-subtle font-semibold mb-0.5">
                                  {categoryName}
                                </div>
                              )}
                              <button
                                className="text-left hover:text-admin-primary transition-colors group"
                                onClick={() =>
                                  setExpandedBioId(isExpanded ? null : bio.id)
                                }
                              >
                                <span className="text-sm text-admin-text-primary group-hover:text-admin-primary">
                                  {bio.name}
                                </span>
                                {bio.unit && (
                                  <span className="text-admin-text-subtle text-sm ml-1">
                                    ({bio.unit})
                                  </span>
                                )}
                                {hasRanges && (
                                  <ChevronRight
                                    className={`inline h-3 w-3 ml-1 text-admin-text-subtle transition-transform ${isExpanded ? "rotate-90" : ""}`}
                                  />
                                )}
                              </button>
                            </td>
                            {visitsWithResults.map((v) => {
                              const results = resultsByVisit.get(v.id) ?? [];
                              const result = results.find(
                                (r) => r.biometricId === bio.id,
                              );
                              const srBg = v.selfReported
                                ? " bg-amber-50/60 dark:bg-amber-900/20"
                                : "";
                              if (!result)
                                return (
                                  <td
                                    key={v.id}
                                    className={`text-center px-1 py-1 text-admin-text-subtle text-sm${srBg} ${showCatHeader ? "border-t border-admin-card-border" : ""}`}
                                  >
                                    -
                                  </td>
                                );
                              const numVal = Number(result.value);
                              const score =
                                !isNaN(numVal) && !bio.isBinary
                                  ? getScore(
                                      numVal,
                                      bio.id,
                                      userGender,
                                      userAge,
                                      refRanges ?? [],
                                    )
                                  : null;
                              // A binary result stores 1 (positive) or 0 (negative).
                              const display = bio.isBinary
                                ? numVal
                                  ? "Positiv"
                                  : "Negativ"
                                : !isNaN(numVal)
                                  ? numVal
                                      .toFixed(2)
                                      .replace(/\.00$/, "")
                                      .replace(/(\.\d)0$/, "$1")
                                  : result.value;
                              return (
                                <td
                                  key={v.id}
                                  className={`text-center px-1 py-1 text-sm${srBg} ${showCatHeader ? "border-t border-admin-card-border" : ""}`}
                                  style={scoreStyle(score)}
                                >
                                  {display}
                                </td>
                              );
                            })}
                          </tr>,
                        );

                        // Reference range detail row
                        if (isExpanded && hasRanges) {
                          rows.push(
                            <tr
                              key={`${bio.id}-ref`}
                              className="bg-admin-bg/50"
                            >
                              <td
                                className="pl-4 pr-2 py-1.5 sticky left-0 bg-admin-bg/50 z-10"
                                colSpan={visitsWithResults.length + 1}
                              >
                                <div className="flex gap-4 text-sm">
                                  {(ranges.optMin || ranges.optMax) && (
                                    <span>
                                      <span
                                        style={{ color: SCORE_COLORS.optimal }}
                                        className="font-medium"
                                      >
                                        Optimal:
                                      </span>{" "}
                                      <span className="text-admin-text-secondary">
                                        {ranges.optMin ?? "?"} –{" "}
                                        {ranges.optMax ?? "?"}
                                      </span>
                                    </span>
                                  )}
                                  {(ranges.normMin || ranges.normMax) && (
                                    <span>
                                      <span
                                        style={{ color: SCORE_COLORS.normal }}
                                        className="font-medium"
                                      >
                                        Normal:
                                      </span>{" "}
                                      <span className="text-admin-text-secondary">
                                        {ranges.normMin ?? "?"} –{" "}
                                        {ranges.normMax ?? "?"}
                                      </span>
                                    </span>
                                  )}
                                  <span className="text-admin-text-subtle">
                                    (
                                    {userGender === "male"
                                      ? "Mann"
                                      : userGender === "female"
                                        ? "Kvinne"
                                        : "Ukjent"}
                                    , {userAge ?? "?"} år)
                                  </span>
                                </div>
                              </td>
                            </tr>,
                          );
                        }
                      }
                      return rows;
                    })()
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* ── Current wearable health ─────────────────────────── */}
        {currentWearable ? (
          <WearableDataCard section={currentWearable} />
        ) : currentWearableLoaded ? (
          <p className="text-[11px] text-admin-text-subtle px-1">
            Ingen wearable-data for de siste 30 dagene.
          </p>
        ) : null}
      </div>

      {/* ── Right: Lab Visit Workflow Panel ─────────────────── */}
      <div className="w-1/3 min-w-[320px] shrink-0 flex flex-col">
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Header + visit selector stick to the top of this scroll
              container — the workflow panel below is long, and which visit
              you're editing has to stay visible the whole way down. */}
          <div className="sticky top-0 z-20 bg-admin-bg pb-2 space-y-2 border-b border-admin-card-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-base font-medium text-admin-text-subtle">
                  Labbesøk
                </h3>
                {selectedVisit && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    {visitFlowLabel(selectedVisit)}
                  </Badge>
                )}
              </div>
              <label className="flex items-center gap-1.5 text-xs text-admin-text-subtle cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={showSelfReported}
                  onChange={() => setShowSelfReported(!showSelfReported)}
                  className="h-3 w-3 rounded border-admin-card-border"
                />
                Vis selvrapporterte
              </label>
            </div>

            {/* Visit selector dropdown */}
            <select
              value={selectedVisit?.id ?? ""}
              onChange={(e) => setSelectedVisitId(e.target.value)}
              className="w-full rounded-md border border-admin-card-border bg-admin-bg px-2.5 py-1.5 text-sm text-admin-text-primary focus:outline-none focus:ring-1 focus:ring-admin-primary"
            >
              {sortedVisits.map((v) => (
                <option key={v.id} value={v.id}>
                  {shortDate(v.visitDate ?? v.createdAt)}
                  {" · "}
                  {/* Replaces the old "(SR)" marker — "Selvrapportert" is one
                      of the labels, so every visit reads the same way. */}
                  {visitFlowLabel(v)}
                  {" — "}
                  {getStatusText(v.status)}
                </option>
              ))}
            </select>
          </div>

          {selectedVisit ? (
            <>
              {/* Customer ↔ doctor chat threads (all visits, current one first).
                  Admin-only for now, matching the Pasientdialog tab. */}
              {userId && hasRole("admin") && (
                <DoctorMessagesPanel
                  authUserId={userId}
                  selectedLabVisitId={selectedVisit.id}
                  autoScroll={searchParams.get("focus") === "meldinger"}
                />
              )}

              {/* Lab visit internal notes */}
              <Card className="border-l-4 border-l-admin-info-500/70">
                <CardContent className="p-0">
                  <button
                    className="w-full flex items-center justify-between p-2.5 hover:bg-admin-card/50"
                    onClick={() => setVisitNotesOpen(!visitNotesOpen)}
                  >
                    <span className="text-xs font-semibold text-admin-info-400 flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5" />
                      Internt labnotat {visitNotes && `(${visitNotes.length})`}
                    </span>
                    {visitNotesOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 text-admin-text-subtle" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-admin-text-subtle" />
                    )}
                  </button>
                  {visitNotesOpen && (
                    <div className="border-t border-admin-card-border p-2.5 space-y-2">
                      {(visitNotes ?? []).length === 0 && (
                        <p className="text-xs text-admin-text-subtle italic">
                          Ingen notater for dette besøket
                        </p>
                      )}
                      {(visitNotes ?? []).map((n) => (
                        <div
                          key={n.id}
                          className="border-l-2 border-l-admin-info-500/50 bg-admin-info-500/5 rounded-r p-2"
                        >
                          <p className="text-xs text-admin-text-primary whitespace-pre-wrap leading-relaxed">
                            {n.note}
                          </p>
                          <p className="text-[10px] text-admin-text-subtle mt-1">
                            {n.author} · {shortDate(n.createdAt)}
                          </p>
                        </div>
                      ))}
                      <div className="flex gap-1.5">
                        <Textarea
                          value={newVisitNote}
                          onChange={(e) => setNewVisitNote(e.target.value)}
                          placeholder="Legg til internt labnotat..."
                          className="min-h-[32px] text-xs flex-1"
                        />
                        <Button
                          size="sm"
                          className="h-8 text-xs"
                          disabled={
                            !newVisitNote.trim() ||
                            createVisitNoteMutation.isPending
                          }
                          onClick={async () => {
                            if (!selectedVisit || !newVisitNote.trim()) return;
                            try {
                              await createVisitNoteMutation.mutateAsync({
                                labVisitId: selectedVisit.id,
                                author: "admin",
                                note: newVisitNote.trim(),
                              });
                              setNewVisitNote("");
                              toast.success("Internt labnotat lagt til");
                            } catch {
                              toast.error("Kunne ikke legge til notat");
                            }
                          }}
                        >
                          Lagre
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* B2B consultation panel — vitals + notes (only renders for B2B visits) */}
              <ConsultationPanel labVisit={selectedVisit} />

              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Current status */}
                  <div>
                    <p className="text-sm text-admin-text-subtle mb-1">
                      Status
                    </p>
                    <Badge
                      variant={
                        selectedVisit.status === "assessmentCompleted"
                          ? "secondary"
                          : "default"
                      }
                    >
                      {getStatusText(selectedVisit.status)}
                    </Badge>
                    {selectedVisit.status === "assessmentCompleted" &&
                      selectedVisit.reviewedByName && (
                        <p className="text-xs text-admin-text-secondary mt-1.5">
                          Godkjent av {selectedVisit.reviewedByName}
                        </p>
                      )}
                  </div>

                  {/* Step progress */}
                  <div className="space-y-1">
                    {STATUS_ORDER.map((status, i) => {
                      const currentStep = getStatusStep(selectedVisit.status);
                      const isComplete = i < currentStep;
                      const isCurrent = i === currentStep;
                      return (
                        <div
                          key={status}
                          className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded ${
                            isCurrent
                              ? "bg-admin-primary/10 text-admin-text-primary font-medium"
                              : isComplete
                                ? "text-admin-success-400"
                                : "text-admin-text-subtle"
                          }`}
                        >
                          <span
                            className={`w-4 h-4 rounded-full border flex items-center justify-center text-[10px] shrink-0 ${
                              isComplete
                                ? "bg-admin-success-500/20 border-admin-success-500 text-admin-success-400"
                                : isCurrent
                                  ? "border-admin-primary bg-admin-primary/20"
                                  : "border-admin-card-border"
                            }`}
                          >
                            {isComplete ? "✓" : i + 1}
                          </span>
                          {getStatusText(status)}
                        </div>
                      );
                    })}
                  </div>

                  {/* Step-specific actions */}
                  {!selectedVisit.selfReported && (
                    <WorkflowActions
                      visit={selectedVisit}
                      allBiometrics={allBiometrics ?? []}
                      biometricPrices={biometricPrices ?? []}
                      bioResults={bioResults ?? []}
                      sortedVisits={sortedVisits}
                      questions={questions ?? []}
                      questionAlternatives={questionAlternatives ?? []}
                      questionNumericRules={questionNumericRules ?? []}
                      questionResponses={questionResponses ?? []}
                      referenceRanges={refRanges ?? []}
                      userGender={userGender}
                      userAge={userAge}
                      userProfileLabId={userProfile?.labId ?? null}
                      onStatusChange={handleStatusChange}
                      onRevertStatus={handleRevertStatus}
                      onStatusChangeWithData={async (id, data) => {
                        await updateLabVisit.mutateAsync({ id, data });
                      }}
                      isPending={
                        updateLabVisit.isPending || overrideStatus.isPending
                      }
                      stickyTarget={stickyTarget}
                    />
                  )}
                  {selectedVisit.selfReported && (
                    <p className="text-sm text-admin-text-subtle italic">
                      Selvrapportert besøk — ingen handlinger
                    </p>
                  )}
                  {!selectedVisit.selfReported && isDevMode() && (
                    <MockLabResultsPanel visit={selectedVisit} />
                  )}
                </CardContent>
              </Card>

              {/* Assessment section — blocked for self-reported visits */}
              {(selectedVisit.status === "allBiometricResultsReceived" ||
                selectedVisit.status === "assessmentCompleted") &&
                !selectedVisit.selfReported && (
                  <AssessmentSection
                    labVisitId={selectedVisit.id}
                    visitDate={selectedVisit.visitDate}
                    variants={assessmentVariants ?? []}
                    isCompleted={selectedVisit.status === "assessmentCompleted"}
                    onApproveVisit={() =>
                      handleStatusChange(
                        selectedVisit.id,
                        "assessmentCompleted",
                      )
                    }
                    isPendingVisit={updateLabVisit.isPending}
                    stickyTarget={stickyTarget}
                    bioResults={(bioResults ?? []).filter(
                      (r) => r.labVisitId === selectedVisit.id,
                    )}
                  />
                )}
            </>
          ) : (
            <p className="text-sm text-admin-text-subtle">Ingen labbesøk</p>
          )}
        </div>
        {/* Sticky footer for primary action */}
        {selectedVisit && !selectedVisit.selfReported && (
          <div
            ref={setStickyTarget}
            className="shrink-0 empty:hidden border-t border-admin-card-border pt-2 mt-1 bg-admin-bg"
          />
        )}
      </div>
    </div>
  );
}

// ── Dev-only: fake incoming lab results ─────────────────────
// Fürst never talks to a dev machine or a PR preview, so the blood-test flow
// would dead-end at "rekvirert". These two buttons synthesise the payload
// Fürst would have POSTed and push it through the real ingest path, so the
// requisition state machine, status sync, notifications and assessment
// generation all run exactly as in production.
//
// Rendered only when dev login is allowed (same condition as the seeded-user
// picker); the backend independently refuses unless ALLOW_MOCK_LAB_RESULTS is
// set and NODE_ENV is not production.

function MockLabResultsPanel({ visit }: { visit: LabVisit }) {
  const { data: requisitions = [] } = useLabVisitRequisitions(visit.id);
  const mockLabResults = useMockLabResults();

  // A fake delivery can only land on a requisition Fürst has been told about.
  const openRequisitions = requisitions.filter(
    (r) => r.status === "submitted" || r.status === "awaitingResults",
  );

  const send = async (mode: "partial" | "all") => {
    try {
      const res = await mockLabResults.mutateAsync({
        labVisitId: visit.id,
        mode,
      });
      const markerCount = res.requisitions.reduce(
        (sum, r) => sum + r.deliveredBiometricIds.length,
        0,
      );
      // "Send alle" only carries the markers still missing a result, so an
      // empty send is normal once everything has already been reported — the
      // endelig-flagget svar is what closes the rekvisisjon.
      toast.success(
        mode === "partial"
          ? `Sendte ${markerCount} delsvar — behandles i bakgrunnen`
          : markerCount > 0
            ? `Sendte ${markerCount} gjenstående prøvesvar (endelig) — behandles i bakgrunnen`
            : "Alle prøvesvar var allerede mottatt — markerer rekvisisjonen som ferdig",
      );
      if (res.skippedBiometricIds.length > 0) {
        toast.warning(
          `${res.skippedBiometricIds.length} biomarkør(er) mangler NLK-kode og kan ikke rapporteres`,
        );
      }
    } catch (e) {
      toast.error(
        e instanceof Error && e.message
          ? e.message
          : "Kunne ikke sende lab-svar",
      );
    }
  };

  return (
    <div className="mt-3 border border-dashed border-admin-card-border rounded p-2 space-y-2">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-3.5 h-3.5 text-admin-text-subtle" />
        <span className="text-xs font-medium text-admin-text-secondary">
          Testverktøy (kun dev/preview)
        </span>
      </div>
      {openRequisitions.length === 0 ? (
        <p className="text-xs text-admin-text-subtle">
          Krever en rekvisisjon som er sendt til Fürst.
        </p>
      ) : (
        <>
          <p className="text-xs text-admin-text-subtle">
            Simulerer prøvesvar fra Fürst for{" "}
            {openRequisitions.length === 1
              ? "rekvisisjonen"
              : "begge rekvisisjonene"}
            . Delsvar sender halvparten av biomarkørene, alle sender resten.
            Verdiene ligger innenfor referanseområdet.
          </p>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-xs h-7"
              disabled={mockLabResults.isPending}
              onClick={() => send("partial")}
            >
              Send delvis lab-svar
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-xs h-7"
              disabled={mockLabResults.isPending}
              onClick={() => send("all")}
            >
              Send alle lab-svar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Workflow action buttons per step ────────────────────────

function WorkflowActions({
  visit,
  allBiometrics,
  biometricPrices,
  bioResults,
  sortedVisits,
  questions,
  questionAlternatives,
  questionNumericRules,
  questionResponses,
  referenceRanges,
  userGender,
  userAge,
  userProfileLabId,
  onStatusChange,
  onRevertStatus,
  onStatusChangeWithData,
  isPending,
  stickyTarget,
}: {
  visit: LabVisit;
  allBiometrics: Biometric[];
  biometricPrices: BiometricPrice[];
  bioResults: BiometricResult[];
  sortedVisits: LabVisit[];
  questions: Question[];
  questionAlternatives: QuestionAlternative[];
  questionNumericRules: QuestionNumericRule[];
  questionResponses: QuestionRespons[];
  referenceRanges: BiometricReferenceRange[];
  userGender?: string | null;
  userAge?: number | null;
  userProfileLabId: string | null;
  onStatusChange: (id: string, status: LabVisitUpdate["status"]) => void;
  onRevertStatus: (
    id: string,
    status: NonNullable<LabVisitUpdate["status"]>,
  ) => void;
  onStatusChangeWithData: (
    id: string,
    data: LabVisitUpdate,
  ) => Promise<void> | void;
  isPending: boolean;
  stickyTarget: HTMLDivElement | null;
}) {
  const s = visit.status;
  const { data: requisitions = [] } = useLabVisitRequisitions(visit.id);
  const { data: labs = [] } = useLabs();
  const hasSubmittedRequisition = requisitions.some(
    (r) => r.status === "submitted",
  );
  // Snail-mail decision uses the visit-scoped lab resolver: visit.labId
  // wins (B2B sets it at creation; lifecycle snapshots profile.labId at
  // requisitioned), with fallback to profile.labId for visits that
  // haven't been requisitioned yet. See lib/utils/resolveVisitLab.ts.
  const labsById = useMemo(() => buildLabsById(labs), [labs]);
  const visitLab = resolveVisitLab(visit, userProfileLabId, labsById);
  const isSnailMailLab = !!visitLab?.snailMailRequisition;

  switch (s) {
    case "paid":
    case "bloodTestRequested":
      return (
        <SuggestBiometricsStep
          visit={visit}
          allBiometrics={allBiometrics}
          biometricPrices={biometricPrices}
          bioResults={bioResults}
          sortedVisits={sortedVisits}
          questions={questions}
          questionAlternatives={questionAlternatives}
          questionNumericRules={questionNumericRules}
          questionResponses={questionResponses}
          referenceRanges={referenceRanges}
          userGender={userGender}
          userAge={userAge}
          onSubmit={onStatusChangeWithData}
          isPending={isPending}
          stickyTarget={stickyTarget}
        />
      );

    case "pendingRequisition":
      if (hasSubmittedRequisition && isSnailMailLab) {
        // Lab requires a physical paper form — admin must mail it and
        // then click "Sett rekvirert" to advance the visit.
        return (
          <SnailMailPendingStep
            visit={visit}
            allBiometrics={allBiometrics}
            onStatusChange={onStatusChange}
            isPending={isPending}
          />
        );
      }
      if (hasSubmittedRequisition) {
        // Non-snail-mail visit with submitted requisitions but stuck at
        // pendingRequisition — sync-lab-visit-status worker either hasn't
        // fired yet or is recovering from a partial-failure split-send.
        // Show the post-send view so the admin sees the submitted state;
        // the worker will catch up and advance the visit shortly.
        return (
          <RequisitionedStep
            visit={visit}
            allBiometrics={allBiometrics}
            onStatusChange={onStatusChange}
            isPending={isPending}
          />
        );
      }
      return (
        <RequisitionStep
          visit={visit}
          allBiometrics={allBiometrics}
          onStatusChange={onStatusChange}
          isPending={isPending}
        />
      );

    case "requisitioned":
      return (
        <RequisitionedStep
          visit={visit}
          allBiometrics={allBiometrics}
          onStatusChange={onStatusChange}
          isPending={isPending}
        />
      );

    case "awaitingResults":
      return (
        <div className="space-y-2">
          <p className="text-sm text-admin-text-secondary">
            Venter på blodprøveresultater fra laboratorium.
          </p>
          <div className="bg-admin-card-border/30 border border-admin-card-border rounded p-2">
            <p className="text-sm text-admin-text-subtle">
              Resultater ikke mottatt ennå
            </p>
          </div>
          <Button
            size="sm"
            className="w-full"
            disabled={isPending}
            onClick={() =>
              onStatusChange(visit.id, "allBiometricResultsReceived")
            }
          >
            Resultater mottatt
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            disabled={isPending}
            onClick={() => onRevertStatus(visit.id, "requisitioned")}
          >
            Tilbakefør
          </Button>
        </div>
      );

    case "allBiometricResultsReceived":
      return (
        <div className="space-y-2">
          <p className="text-sm text-admin-text-secondary">
            Skriv analyse og anbefaling til brukeren. Se vurderinger nedenfor.
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            disabled={isPending}
            onClick={() => onRevertStatus(visit.id, "awaitingResults")}
          >
            Tilbakefør
          </Button>
        </div>
      );

    case "assessmentCompleted":
      return (
        <div className="space-y-2">
          <div className="bg-admin-success-500/10 border border-admin-success-500/20 rounded p-2">
            <p className="text-sm text-admin-success-400">
              Analyse fullført og sendt til bruker
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            disabled={isPending}
            onClick={() =>
              onRevertStatus(visit.id, "allBiometricResultsReceived")
            }
          >
            Tilbakestill for redigering
          </Button>
        </div>
      );

    case "requestRejected":
      return (
        <div className="space-y-2">
          <div className="bg-admin-danger-500/10 border border-admin-danger-500/20 rounded p-2">
            <p className="text-sm text-admin-danger-400">Forespørsel avvist</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            disabled={isPending}
            onClick={() => onRevertStatus(visit.id, "pendingRequisition")}
          >
            Tilbakefør til rekvisisjon
          </Button>
        </div>
      );

    default:
      return null;
  }
}

// ── Suggest Biometrics Step ─────────────────────────────────

// Set equality helper for the continuous-sync effect.
function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function SuggestBiometricsStep({
  visit,
  allBiometrics,
  biometricPrices,
  bioResults,
  sortedVisits,
  questions,
  questionAlternatives,
  questionNumericRules,
  questionResponses,
  referenceRanges,
  userGender,
  userAge,
  onSubmit,
  isPending,
  stickyTarget,
}: {
  visit: LabVisit;
  allBiometrics: Biometric[];
  biometricPrices: BiometricPrice[];
  bioResults: BiometricResult[];
  sortedVisits: LabVisit[];
  questions: Question[];
  questionAlternatives: QuestionAlternative[];
  questionNumericRules: QuestionNumericRule[];
  questionResponses: QuestionRespons[];
  referenceRanges: BiometricReferenceRange[];
  userGender?: string | null;
  userAge?: number | null;
  onSubmit: (id: string, data: LabVisitUpdate) => Promise<void> | void;
  isPending: boolean;
  stickyTarget: HTMLDivElement | null;
}) {
  const [bioSearch, setBioSearch] = useState("");
  const [showBioList, setShowBioList] = useState(true);

  const [helfoUnlockOpen, setHelfoUnlockOpen] = useState(false);

  const {
    engineEval,
    engineLoading,
    rulesVersion,
    dbLoaded,
    helfoReq,
    helfoBioIds,
    nonHelfoBioIds,
    helfoLockedSet,
    effectiveHelfoSet,
    engineHelfoSet,
    currentlyOutsideRange,
    appliedHelfoSubstitutions,
    skippedHistorical,
    helfoReasonNames,
    catalogGapSlugs,
    dedupWarnings,
    activeSources,
    togglePrivat,
    togglePrivatSource,
    rerunEngine,
    rerunPending,
    hasNonEditableRequisition,
    isDrDropinBhtPanel,
    isKickstartPanel,
    isFixedPanel,
    fixedPanelBioIds,
    furstGroupOf,
    isHelfoUnlocked,
    unlockHelfo,
    relockHelfo,
    toggleHelfo,
    needsHelfoInit,
    initializeHelfo,
    clearHelfo,
  } = useLabVisitHelfoPartitioning({
    visit,
    allBiometrics,
    bioResults,
    sortedVisits,
    questions,
    questionAlternatives,
    questionNumericRules,
    questionResponses,
    referenceRanges,
    userGender,
    userAge,
  });

  const fixedPanelName = isDrDropinBhtPanel ? "Dr.Dropin BHT" : "Kickstart";

  const effectiveEngineText = (() => {
    if (!engineEval) return undefined;
    const hasOOR = currentlyOutsideRange.size > 0;
    const hasClusters = engineEval.helfo.active_clusters.length > 0;
    if (!hasClusters && hasOOR)
      return "Kontroll av tidligere unormale prøvesvar.";
    if (hasClusters && hasOOR)
      return `${engineEval.helfo.requisition_text} Inkluderer også tidligere unormale prøvesvar.`;
    return engineEval.helfo.requisition_text;
  })();

  const {
    clinicalInfo,
    setClinicalInfo,
    persist: persistClinicalInfo,
    liveViolations,
    differsFromEngine,
    resetToEngine,
  } = useHelfoClinicalInfoEditor({
    labVisitId: visit.id,
    helfoReq,
    engineRequisitionText: effectiveEngineText,
    dbLoaded,
    engineLoading,
  });

  const priceMap = useMemo(
    () => buildFurstPriceMap(biometricPrices),
    [biometricPrices],
  );

  const getBiosForIds = (ids: Set<string>) =>
    allBiometrics
      .filter((b) => ids.has(b.id))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb"));

  const bioName = (id: string) =>
    allBiometrics.find((b) => b.id === id)?.name ?? id;

  // Non-HELFO partition only — HELFO is patient-free.
  const totalPrice = useMemo(() => {
    const bioMap = new Map(allBiometrics.map((b) => [b.id, b]));
    return sumFurstCost(nonHelfoBioIds, bioMap, priceMap);
  }, [nonHelfoBioIds, priceMap, allBiometrics]);

  const sortedBiometrics = useMemo(
    () =>
      [...allBiometrics].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", "nb"),
      ),
    [allBiometrics],
  );

  const filteredBiometrics = useMemo(() => {
    const q = bioSearch.trim().toLowerCase();
    // For fixed-panel flows (BHT, Kickstart) the panel itself isn't editable,
    // so the resting view is just the markers actually being requisitioned
    // (HELFO + the flow's panel + anything the doctor added on top). Searching
    // opens the full catalog — that's how extra markers get added.
    const base =
      isFixedPanel && !q
        ? sortedBiometrics.filter(
            (b) => effectiveHelfoSet.has(b.id) || nonHelfoBioIds.has(b.id),
          )
        : sortedBiometrics;
    if (!q) return base;
    return base.filter((b) => (b.name ?? "").toLowerCase().includes(q));
  }, [
    sortedBiometrics,
    bioSearch,
    isFixedPanel,
    effectiveHelfoSet,
    nonHelfoBioIds,
  ]);

  // Gated on the draft existing: emptying the HELFO partition deletes it, and
  // the editor's buffer then re-seeds from engine text nobody is sending. The
  // textarea and its violation badges unmount with the draft, so blocking on
  // that buffer left the doctor with a disabled button and nothing to fix.
  const sendBlocked = !!helfoReq && liveViolations.length > 0;

  const handleSend = async () => {
    if (sendBlocked) {
      toast.error(
        "Rett opp kliniske opplysninger før du sender (HELFO-validering feilet).",
      );
      return;
    }
    try {
      await onSubmit(visit.id, {
        status: "pendingRequisition",
      });
      toast.success("Forslag sendt");
    } catch {
      toast.error("Kunne ikke sende forslag");
    }
  };

  const renderSourceToggle = (
    key: keyof typeof activeSources,
    label: string,
  ) => {
    const src = activeSources[key];
    // Hide sources that don't contribute anything for this patient
    // (e.g. no previous visits → previouslyOutsideRange is empty).
    if (src.source.size === 0) return null;
    return (
      <div className="rounded border border-admin-card-border">
        <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-admin-card/50 cursor-pointer">
          <input
            type="checkbox"
            checked={src.selected}
            ref={(el) => {
              if (el) el.indeterminate = src.partial;
            }}
            onChange={() => togglePrivatSource(key)}
            disabled={src.effectiveSource.size === 0}
            className="rounded border-admin-card-border h-3.5 w-3.5"
          />
          <span className="text-xs text-admin-text-primary">{label}</span>
          <span className="text-xs text-admin-text-subtle ml-auto">
            {src.source.size}
          </span>
        </label>
        {/* Pill list always shows the FULL source for transparency.
            HELFO-locked markers render with a lock icon and subdued
            style so the doctor sees where each marker ended up
            without the standard list silently shrinking under them. */}
        {(src.selected || src.effectiveSource.size === 0) &&
          src.source.size > 0 && (
            <div className="px-2 pb-1.5 flex flex-wrap gap-1">
              {getBiosForIds(src.source).map((b) => {
                const inHelfo = effectiveHelfoSet.has(b.id);
                return (
                  <span
                    key={b.id}
                    title={
                      inHelfo ? "Allerede i HELFO-rekvisisjonen" : undefined
                    }
                    className={cn(
                      "text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1",
                      inHelfo
                        ? "bg-admin-primary/10 text-admin-primary/70"
                        : "bg-admin-card text-admin-text-secondary",
                    )}
                  >
                    {inHelfo && <Lock className="h-2.5 w-2.5" />}
                    {b.name}
                  </span>
                );
              })}
            </div>
          )}
      </div>
    );
  };

  // A visit still in `paid` hasn't entered the requisition flow, and the
  // markers endpoint refuses every write on one with 409. The marker UI below
  // would render fully interactive and silently drop every click — which is
  // what an admin-created retake visit looked like. Offer the transition
  // instead; everything below works from `bloodTestRequested` onward.
  if (visit.status === "paid") {
    return (
      <div className="space-y-2">
        <p className="text-sm text-admin-primary font-medium">
          Foreslå blodprøve
        </p>
        <div className="rounded border border-admin-card-border bg-admin-card/40 px-3 py-2.5 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-admin-text-primary font-medium">
              Besøket har ikke bedt om blodprøve ennå
            </p>
            <p className="text-[10px] text-admin-text-subtle mt-0.5">
              Markører kan ikke lagres før besøket er i rekvireringsflyten.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={isPending}
            onClick={async () => {
              try {
                await onSubmit(visit.id, { status: "bloodTestRequested" });
                toast.success("Besøket er klart for rekvirering");
              } catch {
                toast.error("Kunne ikke starte rekvirering");
              }
            }}
            className="gap-1.5 text-[10px] shrink-0"
          >
            Start rekvirering
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-admin-primary font-medium">
        Foreslå blodprøve
      </p>

      {needsHelfoInit && (
        <div className="rounded border border-admin-card-border bg-admin-card/40 px-3 py-2.5 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-admin-text-primary font-medium">
              Lag utkast til rekvisjon
            </p>
            <p className="text-[10px] text-admin-text-subtle mt-0.5">
              Velg regelverk for HELFO-motoren: dagens regler eller HELFO v2
              (september 2026)
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              type="button"
              size="sm"
              onClick={() => initializeHelfo("v1")}
              disabled={engineLoading}
              className="gap-1.5 text-[10px]"
            >
              <Zap
                className={cn("h-3 w-3", engineLoading && "animate-pulse")}
              />
              Dagens regler
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => initializeHelfo("v2")}
              disabled={engineLoading}
              className="gap-1.5 text-[10px]"
            >
              <Zap
                className={cn("h-3 w-3", engineLoading && "animate-pulse")}
              />
              HELFO v2
            </Button>
          </div>
        </div>
      )}

      {!needsHelfoInit && (
      <>
      {/* HELFO partition — single grouped card. Engine status, marker
          contents (engine + currently-OOR), engine-derived warnings, and
          the editable kliniske opplysninger textarea all live here so
          the doctor sees the HELFO requisition as one cohesive thing. */}

      {/* Unlock confirmation dialog — opened by the unlock button below */}
      <Dialog open={helfoUnlockOpen} onOpenChange={setHelfoUnlockOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-admin-warn-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Lås opp HELFO-rekvisisjon
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3 text-sm text-admin-text-secondary">
            <p>
              Kun leger skal redigere HELFO-rekvisisjoner manuelt. Endringer
              overstyrer motorens forslag og lagres direkte.
            </p>
            <p className="text-xs text-admin-text-subtle">
              Du kan låse igjen ved å klikke lås-knappen på nytt.
            </p>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHelfoUnlockOpen(false)}
            >
              Avbryt
            </Button>
            <Button
              size="sm"
              className="bg-admin-warn-600 hover:bg-admin-warn-700 text-white"
              onClick={() => {
                unlockHelfo();
                setHelfoUnlockOpen(false);
              }}
            >
              Lås opp
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div
        className={cn(
          "rounded border bg-admin-primary/5",
          isHelfoUnlocked ? "border-admin-warn-500/50" : "border-admin-primary/30",
        )}
      >
        <div className="px-2 py-1.5 border-b border-admin-primary/20 flex flex-col items-start justify-between gap-2">
          <div>
            <p
              className={cn(
                "text-xs uppercase tracking-wider font-medium flex items-center gap-1.5",
                isHelfoUnlocked ? "text-admin-warn-600" : "text-admin-primary",
              )}
            >
              {isHelfoUnlocked ? (
                <LockOpen className="h-3 w-3" />
              ) : (
                <Lock className="h-3 w-3" />
              )}
              HELFO-rekvisisjon
              {rulesVersion === "v2" && (
                <span className="ml-1 text-[9px] bg-admin-primary/15 text-admin-primary px-1.5 py-0.5 rounded font-medium tracking-normal normal-case">
                  HELFO v2
                </span>
              )}
              {isHelfoUnlocked && (
                <span className="ml-1 text-[9px] bg-admin-warn-500/15 text-admin-warn-600 px-1.5 py-0.5 rounded font-medium tracking-normal normal-case">
                  Legeoverride aktiv
                </span>
              )}
            </p>
            <p className="text-[10px] text-admin-text-subtle mt-0.5">
              {isHelfoUnlocked
                ? "Låst opp — marker-hukinger styrer HELFO direkte"
                : "Låst — bestemt av motor + tidligere unormale resultater"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1 max-w-full shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title={
                isHelfoUnlocked
                  ? "Lås HELFO-rekvisisjonen igjen (tilbake til motorforslag)"
                  : "Lås opp for legeoverride — kun for leger"
              }
              onClick={() => {
                if (isHelfoUnlocked) {
                  relockHelfo();
                } else {
                  setHelfoUnlockOpen(true);
                }
              }}
              className={cn(
                "gap-1 text-[10px]",
                isHelfoUnlocked && "text-admin-warn-600 hover:text-admin-warn-700",
              )}
            >
              {isHelfoUnlocked ? (
                <Lock className="h-3 w-3" />
              ) : (
                <LockOpen className="h-3 w-3" />
              )}
              {isHelfoUnlocked ? "Lås igjen" : "Lås opp"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={hasNonEditableRequisition || !effectiveHelfoSet.size}
              title={
                hasNonEditableRequisition
                  ? "Kan ikke tømme HELFO: en rekvisisjon er allerede innsendt."
                  : "Fjern alle HELFO-markører (for privat rekvisisjon)"
              }
              onClick={() => {
                clearHelfo();
                toast.success("HELFO-markører fjernet");
              }}
              className="gap-1.5 text-[10px] text-destructive hover:text-destructive"
            >
              <Eraser className="h-3 w-3" />
              Tøm HELFO
            </Button>
            {(
              [
                ["v1", "Kjør på nytt: dagens"],
                ["v2", "Kjør på nytt: v2"],
              ] as const
            ).map(([rules, label]) => (
              <Button
                key={rules}
                type="button"
                variant="ghost"
                size="sm"
                disabled={
                  engineLoading ||
                  !engineEval ||
                  !dbLoaded ||
                  rerunPending ||
                  hasNonEditableRequisition
                }
                title={
                  hasNonEditableRequisition
                    ? "Kan ikke kjøre motoren på nytt: en rekvisisjon er allerede innsendt."
                    : `Kjør HELFO-motor på nytt med ${rules === "v2" ? "HELFO v2" : "dagens regler"} og overskriv rekvisisjonene`
                }
                onClick={async () => {
                  try {
                    const { clinicalInfo: newClinicalInfo } =
                      await rerunEngine(rules);
                    // Sync the local editor state so the textarea reflects the
                    // freshly-written DB value (the editor only seeds on first
                    // mount, so it would otherwise show the stale prior text).
                    setClinicalInfo(newClinicalInfo ?? "");
                    toast.success("HELFO-motor kjørt på nytt");
                  } catch (err) {
                    toast.error(
                      err instanceof Error
                        ? err.message
                        : "Kunne ikke kjøre motoren på nytt",
                    );
                  }
                }}
                className="gap-1.5 text-[10px]"
              >
                <RefreshCw
                  className={cn("h-3 w-3", rerunPending && "animate-spin")}
                />
                {label}
              </Button>
            ))}
          </div>
        </div>

        <div className="px-2 py-1.5 space-y-2">
          {/* Engine status — always shown so the doctor never wonders
              whether the engine ran. Three cases:
                - loading
                - no clusters fired (with or without currently-OOR markers)
                - clusters fired (badges) */}
          {engineLoading && (
            <p className="text-xs text-admin-text-subtle">
              Beregner HELFO-forslag …
            </p>
          )}
          {!engineLoading &&
            engineEval &&
            engineEval.helfo.active_clusters.length === 0 &&
            currentlyOutsideRange.size === 0 && (
              <p className="text-xs italic text-admin-text-subtle">
                Ingen indikasjon funnet
              </p>
            )}
          {!engineLoading &&
            engineEval &&
            engineEval.helfo.active_clusters.length === 0 &&
            currentlyOutsideRange.size > 0 && (
              <p className="text-xs italic text-admin-text-subtle">
                Ingen NOKLUS-cluster utløst — HELFO basert på tidligere unormale
                resultater
              </p>
            )}
          {!engineLoading &&
            engineEval &&
            engineEval.helfo.active_clusters.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                  NOKLUS-cluster utløst (
                  {engineEval.helfo.active_clusters.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {engineEval.helfo.cluster_details.map((c) => (
                    <span
                      key={c.cluster_id}
                      className="text-[10px] bg-admin-primary/15 text-admin-primary px-1.5 py-0.5 rounded font-medium"
                      title={c.panel_biomarkers.join(", ")}
                    >
                      {c.cluster_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Fixed-panel marker swaps (fasting → non-fasting glucose and
              triglycerides on BHT and Kickstart). Shown so the doctor isn't
              left wondering why the requisition doesn't match the NOKLUS
              panel verbatim. */}
          {appliedHelfoSubstitutions.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                Byttet ut for {fixedPanelName}
              </p>
              {appliedHelfoSubstitutions.map((sub) => (
                <p
                  key={sub.fromBiometricId}
                  className="text-[10px] text-admin-text-subtle"
                >
                  {bioName(sub.fromBiometricId)} → {bioName(sub.toBiometricId)}{" "}
                  — pasienten er ikke fastende ved denne prøvetakingen.
                </p>
              ))}
            </div>
          )}

          {engineEval && engineEval.helfo.suppressed_clusters.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                Undertrykt
              </p>
              <div className="flex flex-wrap gap-1">
                {engineEval.helfo.suppressed_clusters.map((s) => (
                  <span
                    key={s.cluster_id}
                    className="text-[10px] bg-admin-card px-1.5 py-0.5 rounded text-admin-text-subtle"
                  >
                    {s.cluster_name} ({s.status})
                  </span>
                ))}
              </div>
            </div>
          )}

          {effectiveHelfoSet.size === 0 && !engineLoading ? null : (
            <>
              {(() => {
                // In override mode show only engine markers still present
                // in the override; in normal mode show all engine markers.
                const visibleEngineIds = new Set(
                  [...engineHelfoSet].filter((id) => effectiveHelfoSet.has(id)),
                );
                const visibleOorIds = new Set(
                  [...currentlyOutsideRange].filter((id) =>
                    effectiveHelfoSet.has(id),
                  ),
                );
                // What's left splits two ways: markers that came along with a
                // Fürst code someone else asked for, and markers the doctor
                // actually picked. Attributing the former to the doctor would
                // read as a manual edit to a HELFO requisition they never made.
                const attributedIds = new Set([
                  ...visibleEngineIds,
                  ...visibleOorIds,
                ]);
                const furstSiblingIds = new Set<string>();
                const doctorAddedIds = new Set<string>();
                for (const id of effectiveHelfoSet) {
                  if (attributedIds.has(id)) continue;
                  const cameWithCode = [...furstGroupOf(id)].some(
                    (sibling) => sibling !== id && attributedIds.has(sibling),
                  );
                  if (cameWithCode) furstSiblingIds.add(id);
                  else doctorAddedIds.add(id);
                }
                return (
                  <>
                    {visibleEngineIds.size > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                          Foreslått av motor ({visibleEngineIds.size})
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {getBiosForIds(visibleEngineIds).map((b) => {
                            const clusters = helfoReasonNames.get(b.id) ?? [];
                            return (
                              <span
                                key={b.id}
                                className="text-[10px] bg-admin-card px-1.5 py-0.5 rounded text-admin-text-secondary"
                                title={
                                  clusters.length > 0
                                    ? `Cluster: ${clusters.join(", ")}`
                                    : undefined
                                }
                              >
                                {b.name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {visibleOorIds.size > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                          Utenfor referanseområde på siste måling (
                          {visibleOorIds.size})
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {getBiosForIds(visibleOorIds).map((b) => (
                            <span
                              key={b.id}
                              className="text-[10px] bg-admin-card px-1.5 py-0.5 rounded text-admin-text-secondary"
                              title="Siste resultat var utenfor referanseområde"
                            >
                              {b.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {furstSiblingIds.size > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                          Følger med samme Fürst-analyse (
                          {furstSiblingIds.size})
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {getBiosForIds(furstSiblingIds).map((b) => (
                            <span
                              key={b.id}
                              className="text-[10px] bg-admin-card px-1.5 py-0.5 rounded text-admin-text-secondary"
                              title="Samme analysekode som en markør over — bestilles og betales sammen med den"
                            >
                              {b.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {doctorAddedIds.size > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-admin-warn-600 font-medium">
                          Lagt til av lege ({doctorAddedIds.size})
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {getBiosForIds(doctorAddedIds).map((b) => (
                            <span
                              key={b.id}
                              className="text-[10px] bg-admin-warn-500/15 text-admin-warn-600 px-1.5 py-0.5 rounded"
                            >
                              {b.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* Catalog gaps — engine slugs not yet in Amino catalog. Only
              meaningful for HELFO output. */}
          {catalogGapSlugs.length > 0 && (
            <div className="rounded border border-admin-info/30 bg-admin-info/5 px-2 py-1.5 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-admin-info font-medium">
                Foreslått, men ikke i Amino-katalogen ennå
              </p>
              <div className="flex flex-wrap gap-1">
                {catalogGapSlugs.map((slug) => (
                  <span
                    key={slug}
                    className="text-[10px] bg-admin-card px-1.5 py-0.5 rounded text-admin-text-secondary"
                    title="Ikke tilgjengelig i Amino-katalogen"
                  >
                    {slug}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Historical out-of-range markers we couldn't requisition. */}
          {skippedHistorical.length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-[10px] text-admin-text-subtle hover:text-admin-text-secondary cursor-pointer underline decoration-dotted underline-offset-2"
                >
                  <Info className="h-3 w-3" />
                  {skippedHistorical.length} tidligere unormal
                  {skippedHistorical.length === 1 ? "" : "e"} markør
                  {skippedHistorical.length === 1 ? "" : "er"} kunne ikke
                  rekvireres
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Markører som ikke kunne rekvireres</DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-admin-text-secondary">
                    Disse markørene hadde tidligere verdier utenfor
                    referanseområdet, men kunne ikke legges til i denne
                    rekvisisjonen.
                  </p>
                  <ul className="space-y-1.5">
                    {skippedHistorical.map((entry) => {
                      const bio = allBiometrics.find(
                        (b) => b.id === entry.biometricId,
                      );
                      const reasonText =
                        entry.reason === "not_sendable"
                          ? "Ikke i aktiv Fürst-katalog (mangler furstId)"
                          : entry.reason;
                      return (
                        <li
                          key={entry.biometricId}
                          className="text-xs flex items-baseline justify-between gap-3 border-b border-admin-card-border pb-1.5 last:border-0"
                        >
                          <span className="font-medium text-admin-text-primary">
                            {bio?.name ?? entry.biometricId}
                          </span>
                          <span className="text-admin-text-subtle text-right">
                            {reasonText}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Dedup warnings — soft, doctor can ignore. */}
          {dedupWarnings.length > 0 && (
            <div className="rounded border border-admin-caution-500/40 bg-admin-caution-500/5 px-2 py-1.5 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-admin-caution-700 font-medium">
                Mulig duplikat (nylig tatt)
              </p>
              <ul className="text-xs text-admin-text-secondary space-y-0.5 list-disc list-inside">
                {dedupWarnings.map((w, i) => (
                  <li key={i}>
                    {"biomarker" in w ? w.biomarker : w.panel}: {w.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Editable kliniske opplysninger. Gated on the HELFO
              requisition existing in DB (not just `helfoLockedSet > 0`)
              so the textarea only renders AFTER the continuous-sync
              effect has created the draft + seeded the engine text.
              Otherwise a fast doctor could start typing before the seed
              mutation lands, and the late-arriving mutation would
              overwrite their edit with the engine's auto-generated
              text. */}
          {helfoReq && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-admin-text-subtle font-medium">
                  Kliniske opplysninger
                </p>
                {differsFromEngine && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-admin-warn-500 italic">
                      Avviker fra generert kommentar
                    </span>
                    <button
                      type="button"
                      onClick={resetToEngine}
                      className="text-[10px] text-admin-primary underline hover:no-underline"
                      title={effectiveEngineText}
                    >
                      Oppdater
                    </button>
                  </div>
                )}
              </div>
              <Textarea
                value={clinicalInfo}
                onChange={(e) => setClinicalInfo(e.target.value)}
                onBlur={persistClinicalInfo}
                placeholder="Kliniske opplysninger sendes med HELFO-rekvisisjonen…"
                className={`text-xs min-h-[100px] bg-admin-bg ${differsFromEngine ? "border-admin-warn-400" : ""}`}
              />
              {liveViolations.length > 0 && (
                <div className="space-y-1">
                  {liveViolations.map((v, i) => (
                    <Badge
                      key={i}
                      variant="destructive"
                      className="block w-fit text-[10px]"
                    >
                      {v.type === "forbidden_phrase"
                        ? `Forbudt for HELFO: «${v.phrase}»`
                        : v.message}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Privat partition — doctor-toggleable sources (locked to a fixed panel
          for drDropinBht and kickstart visits). */}
      <div className="space-y-1">
        <p className="text-xs text-admin-text-subtle uppercase tracking-wider font-medium">
          Privat-rekvisisjon{" "}
          <span className="text-[10px] normal-case tracking-normal text-admin-text-subtle">
            {isDrDropinBhtPanel
              ? "(fast panel for Dr.Dropin BHT)"
              : isKickstartPanel
                ? "(fast panel for Kickstart)"
                : "(valgfritt — pasienten betaler)"}
          </span>
        </p>
        {isFixedPanel ? (
          <div className="rounded border border-admin-card-border bg-admin-card/40 px-2 py-1.5 text-[11px] text-admin-text-secondary space-y-1">
            <p>
              <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />
              Fast panel for{" "}
              {isDrDropinBhtPanel ? "Dr.Dropin BHT" : "Kickstart"} — panelets
              egne markører kan ikke fjernes. Markører som lander i
              HELFO-rekvisisjonen fjernes automatisk fra det private panelet.
            </p>
            <p className="text-admin-text-subtle">
              Du kan legge til markører utover panelet: søk i biomarkørlista
              under for å hente fram hele katalogen.
            </p>
          </div>
        ) : (
          <>
            {renderSourceToggle("defaults", "Standardmarkører")}
            {/* Self-hides for first-time patients — no earlier visits, no
                source. */}
            {renderSourceToggle("previousMarkers", "Tidligere tatte markører")}
            {renderSourceToggle("survey", "Fra spørreskjema")}
            {renderSourceToggle(
              "previouslyOutsideRange",
              "Tidligere utenfor referanseområde (nå normalt)",
            )}
            {renderSourceToggle(
              "enginePrivat",
              "Foreslått fra symptomer (privat)",
            )}
          </>
        )}
      </div>

      {/* Selection summary */}
      <button
        className="w-full flex items-center justify-between text-sm"
        onClick={() => setShowBioList(!showBioList)}
      >
        <span className="text-xs text-admin-text-primary font-medium">
          Biomarkører ({effectiveHelfoSet.size + nonHelfoBioIds.size} valgt —{" "}
          {effectiveHelfoSet.size} HELFO, {nonHelfoBioIds.size} privat)
          {totalPrice > 0 && (
            <span className="text-admin-text-subtle font-normal ml-1">
              — {totalPrice.toFixed(0)} kr (privat)
            </span>
          )}
        </span>
        {showBioList ? (
          <ChevronDown className="h-3 w-3 text-admin-text-subtle" />
        ) : (
          <ChevronRight className="h-3 w-3 text-admin-text-subtle" />
        )}
      </button>

      {showBioList && (
        <div
          className={cn(
            "space-y-1 rounded p-1",
            isHelfoUnlocked && "border border-admin-warn-500/50",
          )}
        >
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-admin-text-subtle" />
            <Input
              value={bioSearch}
              onChange={(e) => setBioSearch(e.target.value)}
              placeholder={
                isFixedPanel
                  ? "Søk for å legge til flere biomarkører..."
                  : "Filtrer biomarkører..."
              }
              className="pl-7 h-7 text-xs"
            />
          </div>
          <div className="space-y-0">
            {filteredBiometrics.map((bio) => {
              const isHelfo = effectiveHelfoSet.has(bio.id);
              const isPrivat = nonHelfoBioIds.has(bio.id);
              // On BHT / Kickstart the lock covers the panel's own markers
              // only — extras the doctor added stay removable. In override mode
              // every checkbox is live; moving markers in and out of HELFO is
              // the whole point of unlocking.
              const isPanelMarker = fixedPanelBioIds.has(bio.id);
              const checkboxDisabled = isHelfoUnlocked
                ? false
                : isHelfo || isPanelMarker;
              const clusterNames = helfoReasonNames.get(bio.id) ?? [];
              const helfoTooltip = isHelfo
                ? isHelfoUnlocked
                  ? "HELFO (legeoverride) — klikk for å fjerne"
                  : clusterNames.length > 0
                    ? `HELFO: ${clusterNames.join(", ")}`
                    : currentlyOutsideRange.has(bio.id)
                      ? "HELFO: siste resultat var utenfor referanseområde"
                      : "HELFO (låst)"
                : isHelfoUnlocked
                  ? "Klikk for å legge til i HELFO"
                  : undefined;
              const rowTooltip = isHelfo
                ? helfoTooltip
                : isPanelMarker
                  ? `Fast panel for ${fixedPanelName} — kan ikke fjernes`
                  : (helfoTooltip ??
                    (isFixedPanel
                      ? isPrivat
                        ? "Lagt til utover panelet — klikk for å fjerne"
                        : "Klikk for å legge til utover panelet"
                      : undefined));
              return (
                <div
                  key={bio.id}
                  className={cn(
                    "flex items-center gap-1.5 px-1.5 py-0.5 rounded",
                    checkboxDisabled ? "opacity-90" : "hover:bg-admin-card/50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isHelfoUnlocked ? isHelfo : isHelfo || isPrivat}
                    disabled={checkboxDisabled}
                    onChange={() =>
                      isHelfoUnlocked
                        ? toggleHelfo(bio.id)
                        : togglePrivat(bio.id)
                    }
                    title={rowTooltip}
                    className={cn(
                      "rounded border-admin-card-border h-3 w-3",
                      checkboxDisabled
                        ? "cursor-not-allowed"
                        : "cursor-pointer",
                    )}
                  />
                  <span className="text-xs text-admin-text-primary flex-1">
                    {bio.name}
                  </span>
                  {isHelfo && (
                    <span
                      title={helfoTooltip}
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-medium px-1 py-0.5 rounded",
                        isHelfoUnlocked
                          ? "bg-admin-warn-500/15 text-admin-warn-600"
                          : "bg-admin-primary/15 text-admin-primary",
                      )}
                    >
                      {isHelfoUnlocked ? "HELFO ✏️" : "HELFO 🔒"}
                    </span>
                  )}
                  {isPrivat && !isHelfo && !isHelfoUnlocked && (
                    <span
                      title={
                        isPanelMarker
                          ? `Fast panel for ${fixedPanelName}`
                          : undefined
                      }
                      className="text-[9px] uppercase tracking-wider font-medium px-1 py-0.5 rounded bg-admin-card text-admin-text-subtle"
                    >
                      {isPanelMarker
                        ? `${isDrDropinBhtPanel ? "BHT" : "KICKSTART"} 🔒`
                        : "Privat"}
                    </span>
                  )}
                  {priceMap.has(bio.id) && !isHelfo && (
                    <span className="text-[10px] text-admin-text-subtle">
                      {priceMap.get(bio.id)} kr
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      </>
      )}

      {!needsHelfoInit && stickyTarget &&
        createPortal(
          <Button
            size="sm"
            className="w-full"
            disabled={
              isPending ||
              (effectiveHelfoSet.size === 0 && nonHelfoBioIds.size === 0) ||
              sendBlocked
            }
            onClick={handleSend}
          >
            {sendBlocked
              ? "HELFO-validering må rettes"
              : visit.status === "pendingRequisition"
                ? "Oppdater forslag"
                : "Send forslag til bruker"}
          </Button>,
          stickyTarget,
        )}
    </div>
  );
}

// ── Requisition Step ────────────────────────────────────────

function RequisitionStep({
  visit,
  allBiometrics,
  onStatusChange,
  isPending,
}: {
  visit: LabVisit;
  allBiometrics: Biometric[];
  onStatusChange: (id: string, status: LabVisitUpdate["status"]) => void;
  isPending: boolean;
}) {
  const [showAddBio, setShowAddBio] = useState(false);
  const [addBioSearch, setAddBioSearch] = useState("");
  const [addBioSelected, setAddBioSelected] = useState<Set<string>>(new Set());

  const syncMarkers = useSyncLabVisitMarkers();
  const { data: reqBiometrics = [] } = useLabVisitRequisitionBiometrics(
    visit.id,
  );
  const { data: requisitions = [] } = useLabVisitRequisitions(visit.id);

  // Partition existing markers so we only touch the non-HELFO draft when
  // adding markers from this step (doctor adds late-stage markers; HELFO
  // partition was already decided in SuggestBiometricsStep).
  const nonHelfoReqId = useMemo(
    () =>
      requisitions.find(
        (r) =>
          !r.helfo &&
          (r.status === "draft" || r.status === "readyForSubmission"),
      )?.id ?? null,
    [requisitions],
  );

  const existingNonHelfoBioIds = useMemo(
    () =>
      new Set(
        reqBiometrics
          .filter((rb) => nonHelfoReqId && rb.requisitionId === nonHelfoReqId)
          .map((rb) => rb.biometricId),
      ),
    [reqBiometrics, nonHelfoReqId],
  );

  const existingBioIds = useMemo(
    () => new Set(reqBiometrics.map((rb) => rb.biometricId)),
    [reqBiometrics],
  );

  // Markers on a HELFO requisition, submitted ones included — that analysis
  // code is already ordered and paid for, so nothing may put a copy of it on
  // the privat draft.
  const existingHelfoBioIds = useMemo(() => {
    const helfoReqIds = new Set(
      requisitions.filter((r) => r.helfo).map((r) => r.id),
    );
    return new Set(
      reqBiometrics
        .filter((rb) => helfoReqIds.has(rb.requisitionId))
        .map((rb) => rb.biometricId),
    );
  }, [reqBiometrics, requisitions]);

  const furstIdGroups = useMemo(
    () => buildFurstIdGroups(allBiometrics),
    [allBiometrics],
  );

  const requestedBiometrics = useMemo(
    () =>
      allBiometrics
        .filter((b) => existingBioIds.has(b.id))
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb")),
    [allBiometrics, existingBioIds],
  );

  // Available biomarkers for adding (not already in scope on any draft)
  const availableBiometrics = useMemo(
    () =>
      allBiometrics
        .filter((b) => !existingBioIds.has(b.id))
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb")),
    [allBiometrics, existingBioIds],
  );

  const filteredAddBio = useMemo(() => {
    if (!addBioSearch.trim()) return availableBiometrics;
    const q = addBioSearch.toLowerCase();
    return availableBiometrics.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.shortName?.toLowerCase().includes(q),
    );
  }, [availableBiometrics, addBioSearch]);

  // One Fürst analysis code is one order: picking a marker brings the rest of
  // its code along, exactly as in the suggestion step. The toast counts what
  // actually landed, since a pick can bring siblings or already be covered.
  const handleAddBiometrics = async () => {
    if (addBioSelected.size === 0) return;
    const merged = new Set(existingNonHelfoBioIds);
    for (const id of expandToFurstGroups(addBioSelected, furstIdGroups)) {
      if (!existingHelfoBioIds.has(id)) merged.add(id);
    }
    const addedCount = merged.size - existingNonHelfoBioIds.size;
    try {
      await syncMarkers.mutateAsync({
        labVisitId: visit.id,
        nonHelfoBiometricIds: [...merged],
      });
      toast.success(`${addedCount} biomarkør(er) lagt til`);
      setAddBioSelected(new Set());
      setShowAddBio(false);
      setAddBioSearch("");
    } catch {
      toast.error("Kunne ikke legge til biomarkører");
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-admin-primary font-medium">Rekvirer</p>
      <p className="text-sm text-admin-text-secondary">
        Marker besøket som rekvirert når rekvisisjon er sendt til laboratorium.
      </p>

      {requestedBiometrics.length > 0 && (
        <div className="bg-admin-primary/10 border border-admin-primary/20 rounded p-2">
          <p className="text-xs text-admin-text-subtle mb-1">
            Forespurte biomarkører:
          </p>
          <div className="flex flex-wrap gap-1">
            {requestedBiometrics.map((b) => (
              <span
                key={b.id}
                className="text-xs bg-admin-card px-1.5 py-0.5 rounded text-admin-text-secondary"
              >
                {b.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {!showAddBio ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => setShowAddBio(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Legg til biomarkører
        </Button>
      ) : (
        <div className="border border-admin-card-border rounded p-2 space-y-2">
          <p className="text-xs font-medium text-admin-text-primary">
            Legg til biomarkører ({addBioSelected.size} valgt)
          </p>
          <input
            type="text"
            value={addBioSearch}
            onChange={(e) => setAddBioSearch(e.target.value)}
            placeholder="Søk biomarkører..."
            className="w-full rounded-md border border-admin-card-border bg-admin-bg px-2 py-1 text-xs text-admin-text-primary placeholder:text-admin-text-subtle focus:outline-none focus:ring-1 focus:ring-admin-primary"
          />
          <div className="max-h-[200px] overflow-y-auto space-y-0.5">
            {filteredAddBio.map((b) => (
              <label
                key={b.id}
                className={`flex items-center gap-2 rounded px-1.5 py-1 text-xs cursor-pointer transition-colors ${
                  addBioSelected.has(b.id)
                    ? "bg-admin-primary/10 text-admin-text-primary"
                    : "hover:bg-admin-card/50 text-admin-text-secondary"
                }`}
              >
                <input
                  type="checkbox"
                  checked={addBioSelected.has(b.id)}
                  onChange={() => {
                    const next = new Set(addBioSelected);
                    next.has(b.id) ? next.delete(b.id) : next.add(b.id);
                    setAddBioSelected(next);
                  }}
                  className="rounded border-admin-card-border h-3 w-3"
                />
                <span>{b.name}</span>
                {b.shortName && (
                  <span className="text-admin-text-subtle">
                    ({b.shortName})
                  </span>
                )}
              </label>
            ))}
            {filteredAddBio.length === 0 && (
              <p className="text-xs text-admin-text-subtle text-center py-2">
                Ingen treff
              </p>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 text-xs h-7"
              onClick={() => {
                setShowAddBio(false);
                setAddBioSelected(new Set());
                setAddBioSearch("");
              }}
            >
              Avbryt
            </Button>
            <Button
              size="sm"
              className="flex-1 text-xs h-7"
              disabled={isPending || addBioSelected.size === 0}
              onClick={handleAddBiometrics}
            >
              Legg til ({addBioSelected.size})
            </Button>
          </div>
        </div>
      )}

      <Button
        size="sm"
        className="w-full"
        disabled={isPending}
        onClick={() => onStatusChange(visit.id, "requisitioned")}
      >
        Marker som rekvirert
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="w-full"
        disabled={isPending}
        onClick={() => onStatusChange(visit.id, "paid")}
      >
        Tilbakefør
      </Button>
    </div>
  );
}

// Per-submitted-requisition summary block. Renders helfo flag, marker count,
// price (privat only — HELFO is patient-free), clinical info, and a scoped
// Tillegsrekvirering button. Used by both the post-Send step and the snail-
// mail pending step so split visits show one block per partition.
function RequisitionCard({
  requisition,
  reqBiometrics,
  allBiometrics,
  biometricPrices,
}: {
  requisition: LabVisitRequisition;
  reqBiometrics: { biometricId: string; requisitionId: string }[];
  allBiometrics: Biometric[];
  biometricPrices: BiometricPrice[];
}) {
  const [showEtter, setShowEtter] = useState(false);

  const { bioCount, totalPrice } = useMemo(() => {
    const ids = reqBiometrics
      .filter((rb) => rb.requisitionId === requisition.id)
      .map((rb) => rb.biometricId);
    if (ids.length === 0) return { bioCount: 0, totalPrice: 0 };
    if (requisition.helfo) return { bioCount: ids.length, totalPrice: 0 };
    const bioMap = new Map(allBiometrics.map((b) => [b.id, b]));
    const priceMap = buildFurstPriceMap(biometricPrices);
    return { bioCount: ids.length, totalPrice: sumFurstCost(ids, bioMap, priceMap) };
  }, [
    reqBiometrics,
    requisition.id,
    requisition.helfo,
    allBiometrics,
    biometricPrices,
  ]);

  return (
    <div className="bg-admin-card border border-admin-card-border rounded p-2 space-y-1 text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={requisition.helfo ? "default" : "outline"}>
            {requisition.helfo ? "HELFO" : "Privat"}
          </Badge>
          <span className="text-admin-text-primary font-medium">
            {bioCount} biomarkør{bioCount === 1 ? "" : "er"}
          </span>
        </div>
        {totalPrice > 0 && (
          <span className="text-admin-text-primary font-medium">
            {totalPrice} kr
          </span>
        )}
      </div>
      {requisition.clinicalInfo && (
        <div className="pt-1 border-t border-admin-card-border">
          <p className="text-admin-text-secondary text-xs mb-0.5">
            Kliniske opplysninger
          </p>
          <p className="text-admin-text-primary text-xs">
            {requisition.clinicalInfo}
          </p>
        </div>
      )}
      {requisition.furstComment && (
        <div className="pt-1 border-t border-admin-card-border">
          <p className="text-admin-text-secondary text-xs mb-0.5">
            Melding til Fürst
          </p>
          <p className="text-admin-text-primary text-xs">
            {requisition.furstComment}
          </p>
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        className="w-full mt-1"
        onClick={() => setShowEtter(true)}
      >
        <Plus className="h-3 w-3 mr-1" />
        Tillegsrekvirering
      </Button>
      <EtterrekvireringModal
        open={showEtter}
        onOpenChange={setShowEtter}
        requisitionId={requisition.id}
        allBiometrics={allBiometrics}
      />
    </div>
  );
}

// ── Requisitioned Step (with Tillegsrekvirering) ────────────

function RequisitionedStep({
  visit,
  allBiometrics,
  onStatusChange,
  isPending,
}: {
  visit: LabVisit;
  allBiometrics: Biometric[];
  onStatusChange: (id: string, status: LabVisitUpdate["status"]) => void;
  isPending: boolean;
}) {
  const { data: biometricPrices = [] } = useBiometricPrices();
  const { data: reqBiometrics = [] } = useLabVisitRequisitionBiometrics(
    visit.id,
  );
  const { data: requisitions = [] } = useLabVisitRequisitions(visit.id);
  const submittedRequisitions = requisitions.filter(
    (r) => r.status === "submitted",
  );

  return (
    <div className="space-y-2">
      <p className="text-sm text-admin-text-secondary">
        Blodprøven er rekvirert. Venter på at laboratoriet tar prøver.
      </p>
      {submittedRequisitions.map((req) => (
        <RequisitionCard
          key={req.id}
          requisition={req}
          reqBiometrics={reqBiometrics}
          allBiometrics={allBiometrics}
          biometricPrices={biometricPrices}
        />
      ))}
      <div className="bg-admin-primary/10 border border-admin-primary/20 rounded p-2">
        <p className="text-sm text-admin-primary">
          {submittedRequisitions.length > 1
            ? `${submittedRequisitions.length} rekvisjoner sendt`
            : "Rekvirisjon sendt"}
        </p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="w-full"
        disabled={isPending}
        onClick={() => onStatusChange(visit.id, "pendingRequisition")}
      >
        Tilbakefør
      </Button>
    </div>
  );
}

// ── Snail-mail Pending Step ─────────────────────────────────
//
// For labs that require a physical paper requisition (lab.snailMailRequisition),
// the digital FURST submission alone isn't enough — an admin must mail the
// physical form before the user can have blood drawn. The lab visit stays at
// pendingRequisition during that window, while the requisition is already
// submitted. The marker set is effectively frozen (only etterrekvirering can
// add more), and the doctor's next action is to confirm the physical send.

function SnailMailPendingStep({
  visit,
  allBiometrics,
  onStatusChange,
  isPending,
}: {
  visit: LabVisit;
  allBiometrics: Biometric[];
  onStatusChange: (id: string, status: LabVisitUpdate["status"]) => void;
  isPending: boolean;
}) {
  const { data: biometricPrices = [] } = useBiometricPrices();
  const { data: reqBiometrics = [] } = useLabVisitRequisitionBiometrics(
    visit.id,
  );
  const { data: requisitions = [] } = useLabVisitRequisitions(visit.id);
  const submittedRequisitions = requisitions.filter(
    (r) => r.status === "submitted",
  );

  return (
    <div className="space-y-2">
      <p className="text-sm text-admin-primary font-medium">
        Venter på mailutsendelse av fysisk rekvisjon
      </p>
      <p className="text-sm text-admin-text-secondary">
        Rekvisjon er sendt digitalt. Send fysisk rekvisjon i posten til
        brukeren, og marker som rekvirert når det er gjort.
      </p>
      {submittedRequisitions.map((req) => (
        <RequisitionCard
          key={req.id}
          requisition={req}
          reqBiometrics={reqBiometrics}
          allBiometrics={allBiometrics}
          biometricPrices={biometricPrices}
        />
      ))}
      <Button
        size="sm"
        className="w-full"
        disabled={isPending}
        onClick={() => onStatusChange(visit.id, "requisitioned")}
      >
        Marker som rekvirert
      </Button>
    </div>
  );
}

// ── Assessment section ──────────────────────────────────────

function getVariantName(variant: string): string {
  const names: Record<string, string> = {
    hearthealth: "Hjertehelse",
    stress: "Stress",
    fitness: "Fitness",
    bodyComposition: "Kroppssammensetning",
    sleep: "Søvn",
    other: "Annet",
    emphasizedBiometrics: "Viktige biomarkører",
    reccomendations: "Anbefalinger",
    summary: "Oppsummering",
  };
  return names[variant] ?? variant;
}

function AssessmentSection({
  labVisitId,
  visitDate,
  variants,
  isCompleted,
  onApproveVisit,
  isPendingVisit,
  stickyTarget,
  bioResults,
}: {
  labVisitId: string;
  visitDate: string | null | undefined;
  variants: AssessmentVariant[];
  isCompleted: boolean;
  onApproveVisit: () => void;
  isPendingVisit: boolean;
  stickyTarget: HTMLDivElement | null;
  bioResults: BiometricResult[];
}) {
  const {
    data: assessments,
    isLoading,
    refetch,
  } = useAssessmentsByVisit(labVisitId);
  const createAssessment = useCreateAssessment();
  const updateAssessment = useUpdateAssessment();
  const deleteAssessment = useDeleteAssessment();
  const { data: variantMetadataList } = useAssessmentVariantMetadataList();
  const { data: consultationNotesForVisit } = useConsultationNotes(labVisitId);
  const hasConsultationNotes = (consultationNotesForVisit ?? []).length > 0;
  const { data: wearableSection, isSuccess: wearableLoaded } =
    useWearableDeltas(labVisitId);
  const hasWearableData = !!wearableSection;
  const wearableCoverageLabel = useMemo(() => {
    if (!wearableSection) return undefined;
    // Surface the highest-coverage dense metric (most informative anchor for
    // doctor scanning the banner). Falls back silently if no dense rows.
    const dense = wearableSection.rows.filter((r) => r.mode === "dense_avg");
    if (dense.length === 0) return undefined;
    const best = dense.reduce((a, b) =>
      a.nowCoverage >= b.nowCoverage ? a : b,
    );
    const source = best.nowSources?.join(", ");
    return `${source ? `${source}, ` : ""}${best.nowCoverage}/${best.sliceDays} dager`;
  }, [wearableSection]);
  const [generateAllOpen, setGenerateAllOpen] = useState(false);
  const [regenerateAll, setRegenerateAll] = useState(false);
  const generateAssessments = useGenerateAssessments();
  const [ignoredVariants, setIgnoredVariants] = useState<Set<string>>(
    new Set(),
  );

  // Auto-poll when any assessment is in progress (initiated/processing)
  const hasInProgress = useMemo(
    () =>
      (assessments ?? []).some(
        (a) => a.status === "initiated" || a.status === "processing",
      ),
    [assessments],
  );

  useEffect(() => {
    if (!hasInProgress) return;
    const interval = setInterval(() => refetch(), 5000);
    return () => clearInterval(interval);
  }, [hasInProgress, refetch]);

  const sortedVariants = useMemo(() => {
    return [...variants].sort(
      (a, b) =>
        (VARIANT_ORDER[a.variant] ?? 99) - (VARIANT_ORDER[b.variant] ?? 99),
    );
  }, [variants]);

  // Map variant ID → metadata
  const metadataByVariant = useMemo(() => {
    const map = new Map<
      string,
      typeof variantMetadataList extends (infer T)[] | undefined ? T : never
    >();
    for (const m of variantMetadataList ?? []) {
      map.set(m.assessmentVariantId, m);
    }
    return map;
  }, [variantMetadataList]);

  // Map variant ID to latest non-superseded assessment
  const assessmentByVariant = useMemo(() => {
    const map = new Map<string, Assessment>();
    for (const a of assessments ?? []) {
      if (a.status === "superseded") continue;
      const existing = map.get(a.variantId);
      if (!existing || new Date(a.updatedAt) > new Date(existing.updatedAt)) {
        map.set(a.variantId, a);
      }
    }
    return map;
  }, [assessments]);

  // Retrieved-example (C) prompts differ per variant, so one shared "Vis AI-input"
  // is only shown when every current variant received the same text.
  const { assessmentWithSnapshot, snapshotPerVariant } = useMemo(() => {
    const withSnapshot = [...assessmentByVariant.values()].filter(
      (a) => a.aiInputSnapshot,
    );
    const shared = withSnapshot.every(
      (a) => a.aiInputSnapshot === withSnapshot[0]!.aiInputSnapshot,
    );
    return {
      assessmentWithSnapshot: shared ? (withSnapshot[0] ?? null) : null,
      snapshotPerVariant: !shared,
    };
  }, [assessmentByVariant]);

  const allVariantsComplete = sortedVariants.every((v) => {
    if (ignoredVariants.has(v.id)) return true;
    const a = assessmentByVariant.get(v.id);
    return a?.status === "doctor_approved";
  });

  const pendingCount = sortedVariants.filter((v) => {
    if (ignoredVariants.has(v.id)) return false;
    const a = assessmentByVariant.get(v.id);
    return !a || a.status !== "doctor_approved";
  }).length;

  const hasExistingAssessments = (assessments ?? []).some(
    (a) => a.status !== "superseded",
  );

  const handleGenerate = async (variantId: string, promptAddition?: string) => {
    try {
      await createAssessment.mutateAsync({
        labVisitId,
        variantId,
        status: "initiated",
        result: promptAddition || null,
      });
      toast.success("Analyse startet");
    } catch {
      toast.error("Kunne ikke starte analyse");
    }
  };

  const handleWriteManually = async (variantId: string) => {
    try {
      await createAssessment.mutateAsync({
        labVisitId,
        variantId,
        status: "pending_review",
        result: "",
      });
      toast.success("Manuell analyse opprettet");
    } catch {
      toast.error("Kunne ikke opprette analyse");
    }
  };

  const handleSaveResult = async (assessmentId: string, result: string) => {
    try {
      await updateAssessment.mutateAsync({
        id: assessmentId,
        labVisitId,
        data: { result },
      });
      toast.success("Analyse lagret");
    } catch {
      toast.error("Kunne ikke lagre analyse");
    }
  };

  const handleApprove = async (assessmentId: string, editedText?: string) => {
    try {
      const data: AssessmentUpdate = { status: "doctor_approved" };
      if (editedText !== undefined) data.result = editedText;
      await updateAssessment.mutateAsync({
        id: assessmentId,
        labVisitId,
        data,
      });
      toast.success("Analyse godkjent");
    } catch {
      toast.error("Kunne ikke godkjenne analyse");
    }
  };

  const handleUndoApprove = async (assessmentId: string) => {
    try {
      await updateAssessment.mutateAsync({
        id: assessmentId,
        labVisitId,
        data: { status: "pending_review" },
      });
      toast.success("Godkjenning angret");
    } catch {
      toast.error("Kunne ikke angre godkjenning");
    }
  };

  const handleDelete = async (assessmentId: string) => {
    try {
      await deleteAssessment.mutateAsync({ id: assessmentId, labVisitId });
      toast.success("Analyse slettet");
    } catch {
      toast.error("Kunne ikke slette analyse");
    }
  };

  const handleReanalyze = async (
    variantId: string,
    existingAssessmentId: string,
    promptAddition?: string,
  ) => {
    try {
      await updateAssessment.mutateAsync({
        id: existingAssessmentId,
        labVisitId,
        data: { status: "superseded" },
      });
      await createAssessment.mutateAsync({
        labVisitId,
        variantId,
        status: "initiated",
        result: promptAddition || null,
      });
      toast.success("Revurdering startet");
    } catch {
      toast.error("Kunne ikke starte revurdering");
    }
  };

  const handleGenerateAll = async (
    params: GenerateAllParams,
    regenerate = false,
  ) => {
    try {
      await generateAssessments.mutateAsync({
        labVisitId,
        pipeline: params.pipeline,
        examples: params.examples,
        model: params.model,
        regenerate,
      });
      toast.success("Generering av alle varianter startet");
      setTimeout(() => refetch(), 2000);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Kunne ikke starte generering",
      );
    }
  };

  const handleRegenerateAll = async (params: GenerateAllParams) => {
    await handleGenerateAll(params, true);
  };

  const toggleIgnored = (variantId: string) => {
    setIgnoredVariants((prev) => {
      const next = new Set(prev);
      next.has(variantId) ? next.delete(variantId) : next.add(variantId);
      return next;
    });
  };

  const anyPending =
    createAssessment.isPending ||
    updateAssessment.isPending ||
    deleteAssessment.isPending ||
    generateAssessments.isPending;

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-medium text-admin-text-subtle">
          Vurderinger
        </h4>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-6 px-2"
            disabled={anyPending}
            onClick={() => {
              setRegenerateAll(false);
              setGenerateAllOpen(true);
            }}
          >
            Generer alle
          </Button>
          {hasExistingAssessments && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-6 px-2"
              disabled={anyPending}
              onClick={() => {
                setRegenerateAll(true);
                setGenerateAllOpen(true);
              }}
            >
              Regenerer alle
            </Button>
          )}
          <button
            className="h-6 w-6 flex items-center justify-center rounded hover:bg-admin-card text-admin-text-subtle"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      </div>

      <GenerateAllDialog
        open={generateAllOpen}
        onOpenChange={setGenerateAllOpen}
        onGenerateAll={regenerateAll ? handleRegenerateAll : handleGenerateAll}
        regenerate={regenerateAll}
        variantCount={sortedVariants.length}
      />

      <AssessmentAuditButtons
        assessment={assessmentWithSnapshot}
        hasConsultationNotes={hasConsultationNotes}
        hasWearableData={hasWearableData}
        wearableCoverageLabel={wearableCoverageLabel}
        variantLabel="Alle varianter"
      />

      {wearableSection ? (
        <WearableDataCard section={wearableSection} />
      ) : wearableLoaded ? (
        <p className="text-xs text-admin-text-subtle px-1">
          Ingen wearable-data tilgjengelig for dette besøkets tidsvindu
          {visitDate
            ? ` (besøksdato ${new Date(visitDate).toLocaleDateString("nb-NO")})`
            : ""}
          . Data logget etter blodprøvedatoen er ikke inkludert.
        </p>
      ) : null}

      {/* Approve visit button */}
      {!isCompleted && (
        <Button
          size="sm"
          className="w-full"
          disabled={!allVariantsComplete || isPendingVisit}
          onClick={onApproveVisit}
        >
          {allVariantsComplete
            ? "Godkjenn besøk"
            : `Venter på ${pendingCount} varianter`}
        </Button>
      )}

      {sortedVariants.map((variant) => {
        const assessment = assessmentByVariant.get(variant.id);
        const metadata = metadataByVariant.get(variant.id) ?? null;
        const isIgnored = ignoredVariants.has(variant.id);

        if (variant.variant === "emphasizedBiometrics") {
          return (
            <EmphasizedBiometricsCard
              key={variant.id}
              variant={variant}
              assessment={assessment ?? null}
              labVisitId={labVisitId}
              isCompleted={isCompleted}
              onGenerate={(prompt) => handleGenerate(variant.id, prompt)}
              onWriteManually={() => handleWriteManually(variant.id)}
              onApprove={() => handleApprove(assessment!.id)}
              onUndoApprove={() => handleUndoApprove(assessment!.id)}
              onDelete={() => handleDelete(assessment!.id)}
              onReanalyze={(prompt) =>
                handleReanalyze(variant.id, assessment!.id, prompt)
              }
              isPending={anyPending}
              metadata={metadata}
              isIgnored={isIgnored}
              onToggleIgnored={() => toggleIgnored(variant.id)}
              showAiInput={snapshotPerVariant}
              bioResults={bioResults}
            />
          );
        }

        if (variant.variant === "reccomendations") {
          return (
            <RecommendationsCard
              key={variant.id}
              variant={variant}
              assessment={assessment ?? null}
              labVisitId={labVisitId}
              isCompleted={isCompleted}
              onGenerate={(prompt) => handleGenerate(variant.id, prompt)}
              onWriteManually={() => handleWriteManually(variant.id)}
              onApprove={() => handleApprove(assessment!.id)}
              onUndoApprove={() => handleUndoApprove(assessment!.id)}
              onDelete={() => handleDelete(assessment!.id)}
              onReanalyze={(prompt) =>
                handleReanalyze(variant.id, assessment!.id, prompt)
              }
              isPending={anyPending}
              metadata={metadata}
              isIgnored={isIgnored}
              onToggleIgnored={() => toggleIgnored(variant.id)}
              showAiInput={snapshotPerVariant}
            />
          );
        }

        return (
          <AssessmentCard
            key={variant.id}
            variant={variant}
            assessment={assessment ?? null}
            isCompleted={isCompleted}
            onGenerate={(prompt) => handleGenerate(variant.id, prompt)}
            onWriteManually={() => handleWriteManually(variant.id)}
            onSave={(result) => handleSaveResult(assessment!.id, result)}
            onApprove={(editedText) =>
              handleApprove(assessment!.id, editedText)
            }
            onUndoApprove={() => handleUndoApprove(assessment!.id)}
            onDelete={() => handleDelete(assessment!.id)}
            onReanalyze={(prompt) =>
              handleReanalyze(variant.id, assessment!.id, prompt)
            }
            isPending={anyPending}
            metadata={metadata}
            isIgnored={isIgnored}
            onToggleIgnored={() => toggleIgnored(variant.id)}
            showAiInput={snapshotPerVariant}
          />
        );
      })}
      {sortedVariants.length === 0 && (
        <p className="text-sm text-admin-text-subtle">
          Ingen analysevarianter konfigurert
        </p>
      )}
      {stickyTarget &&
        !isCompleted &&
        createPortal(
          <Button
            size="sm"
            className="w-full"
            disabled={!allVariantsComplete || isPendingVisit}
            onClick={onApproveVisit}
          >
            {allVariantsComplete
              ? "Godkjenn besøk"
              : `Venter på ${pendingCount} varianter`}
          </Button>,
          stickyTarget,
        )}
    </div>
  );
}

const EXAMPLE_TAGS = [
  { tag: "exemplary", Icon: Star, label: "Godt eksempel" },
  { tag: "edge_case", Icon: Puzzle, label: "Spesialtilfelle" },
  { tag: "exclude", Icon: Ban, label: "Ikke bruk som eksempel" },
] as const;

/**
 * The doctor's verdict on an approved analysis as an AI example; click again to
 * clear. A special case gets a note on what made it special.
 */
function ExampleTagButtons({ assessment }: { assessment: Assessment }) {
  const updateAssessment = useUpdateAssessment();
  const [note, setNote] = useState(assessment.exampleNote ?? "");
  useEffect(() => setNote(assessment.exampleNote ?? ""), [assessment.exampleNote]);

  const save = async (data: AssessmentUpdate) => {
    try {
      await updateAssessment.mutateAsync({
        id: assessment.id,
        labVisitId: assessment.labVisitId,
        data,
      });
    } catch {
      toast.error("Kunne ikke lagre merkingen");
    }
  };
  // The server clears the note when the tag stops being an edge case.
  const setTag = (tag: (typeof EXAMPLE_TAGS)[number]["tag"]) =>
    save({ exampleTag: assessment.exampleTag === tag ? null : tag });
  const saveNote = () => {
    const value = note.trim() || null;
    if (value !== (assessment.exampleNote ?? null)) save({ exampleNote: value });
  };

  return (
    <div className="flex items-center">
      {assessment.exampleTag === "edge_case" && (
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          placeholder="Hva var spesielt?"
          aria-label="Hva var spesielt med denne analysen?"
          className="mr-1 h-6 w-56 rounded border border-admin-card-border bg-transparent px-1.5 text-xs text-admin-text-primary placeholder:text-admin-text-subtle"
        />
      )}
      {EXAMPLE_TAGS.map(({ tag, Icon, label }) => {
        const active = assessment.exampleTag === tag;
        return (
          <button
            key={tag}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            disabled={updateAssessment.isPending}
            onClick={() => setTag(tag)}
            className={`inline-flex h-6 w-6 items-center justify-center rounded ${active ? "text-admin-primary" : "text-admin-text-subtle opacity-50 hover:opacity-100"}`}
          >
            <Icon className={`h-3.5 w-3.5 ${active ? "fill-current" : ""}`} />
          </button>
        );
      })}
    </div>
  );
}

function AssessmentCard({
  variant,
  assessment,
  isCompleted,
  onGenerate,
  onWriteManually,
  onSave,
  onApprove,
  onUndoApprove,
  onDelete,
  onReanalyze,
  isPending,
  metadata,
  isIgnored,
  onToggleIgnored,
  showAiInput,
}: {
  variant: AssessmentVariant;
  assessment: Assessment | null;
  isCompleted: boolean;
  onGenerate: (prompt?: string) => void;
  onWriteManually: () => void;
  onSave: (result: string) => void;
  onApprove: (editedText?: string) => void;
  onUndoApprove: () => void;
  onDelete: () => void;
  onReanalyze: (prompt?: string) => void;
  isPending: boolean;
  metadata: import("@/lib/api_client").AssessmentVariantMetadata | null;
  isIgnored: boolean;
  onToggleIgnored: () => void;
  showAiInput: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [reanalyzeDialogOpen, setReanalyzeDialogOpen] = useState(false);

  const statusLabel: Record<string, string> = {
    initiated: "Starter...",
    processing: "Genererer...",
    pending_review: "Til godkjenning",
    doctor_approved: "Godkjent",
    superseded: "Erstattet",
  };

  const statusColor: Record<string, string> = {
    initiated: "text-admin-text-subtle",
    processing: "text-admin-primary",
    pending_review: "text-admin-caution-400",
    doctor_approved: "text-admin-success-400",
  };

  const startEdit = () => {
    setEditText(assessment?.result ?? "");
    setEditing(true);
  };

  const saveEdit = () => {
    onSave(editText);
    setEditing(false);
  };

  return (
    <Card className={isIgnored ? "opacity-50" : ""}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label
              className="flex items-center gap-1.5 cursor-pointer"
              title="Ignorer denne varianten"
            >
              <input
                type="checkbox"
                checked={isIgnored}
                onChange={onToggleIgnored}
                className="h-3 w-3 rounded border-admin-card-border"
              />
            </label>
            <span className="text-sm font-medium text-admin-text-primary">
              {getVariantName(variant.variant)}
            </span>
          </div>
          {assessment && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">
                {[
                  assessment.pipelineVersion,
                  assessment.aiModel,
                  assessment.pipelineVersion === "v2" ? `examples=${assessment.examples}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Badge>
              {showAiInput && (
                <AssessmentAuditButtons
                  assessment={assessment}
                  hasConsultationNotes={false}
                  variantLabel={getVariantName(variant.variant)}
                />
              )}
              {assessment.status === "doctor_approved" && (
                <ExampleTagButtons assessment={assessment} />
              )}
              {assessment.status === "doctor_approved" &&
                assessment.reviewedByName && (
                  <span className="text-xs text-admin-text-subtle">
                    {assessment.reviewedByName}
                  </span>
                )}
              <span
                className={`text-xs ${statusColor[assessment.status] ?? "text-admin-text-subtle"}`}
              >
                {statusLabel[assessment.status] ?? assessment.status}
              </span>
            </div>
          )}
        </div>

        {!assessment ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-sm h-8"
              disabled={isPending}
              onClick={() => setGenerateDialogOpen(true)}
            >
              Generer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 text-sm h-8"
              disabled={isPending}
              onClick={onWriteManually}
            >
              Skriv manuelt
            </Button>
            <AdvancedParamsDialog
              open={generateDialogOpen}
              onOpenChange={setGenerateDialogOpen}
              onGenerate={(params) => onGenerate(params.promptAddition)}
              defaultMetadata={metadata}
              variantName={getVariantName(variant.variant)}
            />
          </div>
        ) : assessment.status === "initiated" ||
          assessment.status === "processing" ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-admin-text-subtle">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Genererer analyse...
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-sm h-8"
              disabled={isPending}
              onClick={onDelete}
            >
              Avbryt
            </Button>
          </div>
        ) : assessment.status === "pending_review" ? (
          <div className="space-y-2">
            {editing ? (
              <>
                <Textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="min-h-[300px] max-h-[60vh] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 text-sm h-8"
                    disabled={isPending}
                    onClick={saveEdit}
                  >
                    Lagre
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8"
                    onClick={() => setEditing(false)}
                  >
                    Avbryt
                  </Button>
                </div>
              </>
            ) : (
              <>
                {assessment.result ? (
                  <p className="text-sm text-admin-text-secondary whitespace-pre-wrap border border-admin-card-border rounded p-2">
                    {assessment.result}
                  </p>
                ) : (
                  <p className="text-sm text-admin-text-subtle italic">
                    Ingen tekst ennå
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 text-sm h-8"
                    disabled={isPending}
                    onClick={() => onApprove()}
                  >
                    Godkjenn
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-sm h-8"
                    onClick={startEdit}
                  >
                    Rediger
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8 text-admin-text-subtle"
                    onClick={() => setReanalyzeDialogOpen(true)}
                  >
                    Reanalyser
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8 text-admin-text-subtle"
                    disabled={isPending}
                    onClick={onDelete}
                  >
                    Slett
                  </Button>
                </div>
                <AdvancedParamsDialog
                  open={reanalyzeDialogOpen}
                  onOpenChange={setReanalyzeDialogOpen}
                  onGenerate={(params) => onReanalyze(params.promptAddition)}
                  defaultMetadata={metadata}
                  variantName={getVariantName(variant.variant)}
                  isReanalysis
                  existingText={assessment.result ?? ""}
                />
              </>
            )}
          </div>
        ) : assessment.status === "doctor_approved" ? (
          <div className="space-y-2">
            {assessment.result && (
              <p className="text-sm text-admin-text-secondary whitespace-pre-wrap border border-admin-success-500/20 rounded p-2">
                {assessment.result}
              </p>
            )}
            {!isCompleted && (
              <Button
                size="sm"
                variant="ghost"
                className="w-full text-sm h-8"
                disabled={isPending}
                onClick={onUndoApprove}
              >
                Angre godkjenning
              </Button>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── Emphasized Biometrics Card ───────────────────────────────

function EmphasizedBiometricsCard({
  variant,
  assessment,
  labVisitId,
  isCompleted,
  onGenerate,
  onWriteManually,
  onApprove,
  onUndoApprove,
  onDelete,
  onReanalyze,
  isPending,
  metadata,
  isIgnored,
  onToggleIgnored,
  showAiInput,
  bioResults,
}: {
  variant: AssessmentVariant;
  assessment: Assessment | null;
  labVisitId: string;
  isCompleted: boolean;
  onGenerate: (prompt?: string) => void;
  onWriteManually: () => void;
  onApprove: () => void;
  onUndoApprove: () => void;
  onDelete: () => void;
  onReanalyze: (prompt?: string) => void;
  isPending: boolean;
  metadata: import("@/lib/api_client").AssessmentVariantMetadata | null;
  isIgnored: boolean;
  onToggleIgnored: () => void;
  showAiInput: boolean;
  bioResults: BiometricResult[];
}) {
  const { data: items, refetch } = useEmphasizedBiometrics(
    assessment?.id ?? null,
  );
  const { data: allBiometrics } = useBiometrics();
  const updateItem = useUpdateEmphasizedBiometric();
  const createItem = useCreateEmphasizedBiometric();
  const deleteItem = useDeleteEmphasizedBiometric();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBiometricId, setNewBiometricId] = useState("");
  const [newText, setNewText] = useState("");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [reanalyzeDialogOpen, setReanalyzeDialogOpen] = useState(false);

  const statusLabel: Record<string, string> = {
    initiated: "Starter...",
    processing: "Genererer...",
    pending_review: "Til godkjenning",
    doctor_approved: "Godkjent",
    superseded: "Erstattet",
  };

  const statusColor: Record<string, string> = {
    initiated: "text-admin-text-subtle",
    processing: "text-admin-primary",
    pending_review: "text-admin-caution-400",
    doctor_approved: "text-admin-success-400",
  };

  const sortedItems = useMemo(() => {
    if (!items) return [];
    return [...items].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [items]);

  const sortedBiometrics = useMemo(() => {
    if (!allBiometrics) return [];
    const testedIds = new Set(bioResults.map((r) => r.biometricId));
    return allBiometrics
      .filter((b) => testedIds.has(b.id))
      .sort((a, b) => a.name.localeCompare(b.name, "nb"));
  }, [allBiometrics, bioResults]);

  const startEdit = (item: AssessmentEmphasizedBiometric) => {
    setEditingId(item.id);
    setEditText(item.text);
    setConfirmDeleteId(null);
  };

  const saveEdit = async (item: AssessmentEmphasizedBiometric) => {
    try {
      await updateItem.mutateAsync({
        id: item.id,
        data: { text: editText },
        assessmentId: item.assessmentId,
      });
      setEditingId(null);
      toast.success("Biomarkør oppdatert");
    } catch {
      toast.error("Kunne ikke oppdatere");
    }
  };

  const handleDelete = async (item: AssessmentEmphasizedBiometric) => {
    try {
      await deleteItem.mutateAsync({
        id: item.id,
        assessmentId: item.assessmentId,
      });
      setConfirmDeleteId(null);
      toast.success("Biomarkør fjernet");
      refetch();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const handleAdd = async () => {
    if (!assessment || !newBiometricId) return;
    const bio = sortedBiometrics.find((b) => b.id === newBiometricId);
    if (!bio) return;
    try {
      await createItem.mutateAsync({
        data: {
          assessmentId: assessment.id,
          biometricId: bio.id,
          biometricName: bio.name,
          text: newText,
        },
        assessmentId: assessment.id,
      });
      setShowAddForm(false);
      setNewBiometricId("");
      setNewText("");
      toast.success("Biomarkør lagt til");
    } catch {
      toast.error("Kunne ikke legge til");
    }
  };

  return (
    <Card className={isIgnored ? "opacity-50" : ""}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label
              className="flex items-center gap-1.5 cursor-pointer"
              title="Ignorer denne varianten"
            >
              <input
                type="checkbox"
                checked={isIgnored}
                onChange={onToggleIgnored}
                className="h-3 w-3 rounded border-admin-card-border"
              />
            </label>
            <span className="text-sm font-medium text-admin-text-primary">
              {getVariantName(variant.variant)}
            </span>
          </div>
          {assessment && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">
                {[
                  assessment.pipelineVersion,
                  assessment.aiModel,
                  assessment.pipelineVersion === "v2" ? `examples=${assessment.examples}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Badge>
              {showAiInput && (
                <AssessmentAuditButtons
                  assessment={assessment}
                  hasConsultationNotes={false}
                  variantLabel={getVariantName(variant.variant)}
                />
              )}
              {assessment.status === "doctor_approved" && (
                <ExampleTagButtons assessment={assessment} />
              )}
              {assessment.status === "doctor_approved" &&
                assessment.reviewedByName && (
                  <span className="text-xs text-admin-text-subtle">
                    {assessment.reviewedByName}
                  </span>
                )}
              <span
                className={`text-xs ${statusColor[assessment.status] ?? "text-admin-text-subtle"}`}
              >
                {statusLabel[assessment.status] ?? assessment.status}
              </span>
            </div>
          )}
        </div>

        {!assessment ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-sm h-8"
              disabled={isPending}
              onClick={() => setGenerateDialogOpen(true)}
            >
              Generer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 text-sm h-8"
              disabled={isPending}
              onClick={onWriteManually}
            >
              Skriv manuelt
            </Button>
            <AdvancedParamsDialog
              open={generateDialogOpen}
              onOpenChange={setGenerateDialogOpen}
              onGenerate={(params) => onGenerate(params.promptAddition)}
              defaultMetadata={metadata}
              variantName={getVariantName(variant.variant)}
            />
          </div>
        ) : assessment.status === "initiated" ||
          assessment.status === "processing" ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-admin-text-subtle">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Genererer biomarkører...
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-sm h-8"
              disabled={isPending}
              onClick={onDelete}
            >
              Avbryt
            </Button>
          </div>
        ) : assessment.status === "pending_review" ||
          assessment.status === "doctor_approved" ? (
          <div className="space-y-2">
            {sortedItems.length > 0 ? (
              <div className="space-y-2">
                {sortedItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-admin-card-border rounded p-2 space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-admin-primary">
                        {item.biometricName}
                      </span>
                      {assessment.status === "pending_review" &&
                        editingId !== item.id && (
                          <div className="flex items-center gap-2">
                            {confirmDeleteId === item.id ? (
                              <>
                                <span className="text-xs text-admin-text-subtle">
                                  Slett?
                                </span>
                                <button
                                  className="text-xs text-admin-danger-400 hover:text-admin-danger-300 border border-admin-danger-400 hover:border-admin-danger-300 rounded px-1.5 py-0.5"
                                  onClick={() => handleDelete(item)}
                                >
                                  Ja
                                </button>
                                <span className="text-admin-card-border">
                                  |
                                </span>
                                <button
                                  className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                                  onClick={() => setConfirmDeleteId(null)}
                                >
                                  Nei
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                                  onClick={() => {
                                    setConfirmDeleteId(item.id);
                                    setEditingId(null);
                                  }}
                                >
                                  Slett
                                </button>
                                <button
                                  className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                                  onClick={() => startEdit(item)}
                                >
                                  Rediger
                                </button>
                              </>
                            )}
                          </div>
                        )}
                    </div>
                    {editingId === item.id ? (
                      <div className="space-y-1">
                        <Textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="min-h-[100px] max-h-[40vh] text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 text-xs h-7"
                            disabled={updateItem.isPending}
                            onClick={() => saveEdit(item)}
                          >
                            Lagre
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="flex-1 text-xs h-7"
                            onClick={() => setEditingId(null)}
                          >
                            Avbryt
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-admin-text-secondary whitespace-pre-wrap">
                        {item.text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-admin-text-subtle italic">
                Ingen biomarkører generert
              </p>
            )}

            {assessment.status === "pending_review" && (
              <>
                {showAddForm ? (
                  <div className="border border-admin-card-border rounded p-2 space-y-2">
                    <select
                      value={newBiometricId}
                      onChange={(e) => setNewBiometricId(e.target.value)}
                      className="w-full text-sm h-8 rounded border border-admin-card-border bg-admin-card text-admin-text-primary px-2"
                    >
                      <option value="">Velg biomarkør...</option>
                      {sortedBiometrics.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    <Textarea
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      placeholder="Beskrivelse..."
                      className="min-h-[80px] text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 text-xs h-7"
                        disabled={!newBiometricId || createItem.isPending}
                        onClick={handleAdd}
                      >
                        Legg til
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 text-xs h-7"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewBiometricId("");
                          setNewText("");
                        }}
                      >
                        Avbryt
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full text-sm h-8 text-admin-text-subtle"
                    onClick={() => setShowAddForm(true)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Legg til biomarkør
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 text-sm h-8"
                    disabled={isPending}
                    onClick={onApprove}
                  >
                    Godkjenn
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8 text-admin-text-subtle"
                    onClick={() => setReanalyzeDialogOpen(true)}
                  >
                    Reanalyser
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8 text-admin-text-subtle"
                    disabled={isPending}
                    onClick={onDelete}
                  >
                    Slett
                  </Button>
                </div>
                <AdvancedParamsDialog
                  open={reanalyzeDialogOpen}
                  onOpenChange={setReanalyzeDialogOpen}
                  onGenerate={(params) => onReanalyze(params.promptAddition)}
                  defaultMetadata={metadata}
                  variantName={getVariantName(variant.variant)}
                  isReanalysis
                />
              </>
            )}
            {assessment.status === "doctor_approved" && !isCompleted && (
              <Button
                size="sm"
                variant="ghost"
                className="w-full text-sm h-8"
                disabled={isPending}
                onClick={onUndoApprove}
              >
                Angre godkjenning
              </Button>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── Recommendations Card ─────────────────────────────────────

function RecommendationsCard({
  variant,
  assessment,
  labVisitId,
  isCompleted,
  onGenerate,
  onWriteManually,
  onApprove,
  onUndoApprove,
  onDelete,
  onReanalyze,
  isPending,
  metadata,
  isIgnored,
  onToggleIgnored,
  showAiInput,
}: {
  variant: AssessmentVariant;
  assessment: Assessment | null;
  labVisitId: string;
  isCompleted: boolean;
  onGenerate: (prompt?: string) => void;
  onWriteManually: () => void;
  onApprove: () => void;
  onUndoApprove: () => void;
  onDelete: () => void;
  onReanalyze: (prompt?: string) => void;
  isPending: boolean;
  metadata: import("@/lib/api_client").AssessmentVariantMetadata | null;
  isIgnored: boolean;
  onToggleIgnored: () => void;
  showAiInput: boolean;
}) {
  const { data: items, refetch } = useRecommendations(assessment?.id ?? null);
  const updateItem = useUpdateRecommendation();
  const createItem = useCreateRecommendation();
  const deleteItem = useDeleteRecommendation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [reanalyzeDialogOpen, setReanalyzeDialogOpen] = useState(false);

  const statusLabel: Record<string, string> = {
    initiated: "Starter...",
    processing: "Genererer...",
    pending_review: "Til godkjenning",
    doctor_approved: "Godkjent",
    superseded: "Erstattet",
  };

  const statusColor: Record<string, string> = {
    initiated: "text-admin-text-subtle",
    processing: "text-admin-primary",
    pending_review: "text-admin-caution-400",
    doctor_approved: "text-admin-success-400",
  };

  const sortedItems = useMemo(() => {
    if (!items) return [];
    return [...items].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [items]);

  const startEdit = (item: AssessmentRecommendation) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditDesc(item.description);
    setConfirmDeleteId(null);
  };

  const saveEdit = async (item: AssessmentRecommendation) => {
    try {
      await updateItem.mutateAsync({
        id: item.id,
        data: { title: editTitle, description: editDesc },
        assessmentId: item.assessmentId,
      });
      setEditingId(null);
      toast.success("Anbefaling oppdatert");
    } catch {
      toast.error("Kunne ikke oppdatere");
    }
  };

  const handleDelete = async (item: AssessmentRecommendation) => {
    try {
      await deleteItem.mutateAsync({
        id: item.id,
        assessmentId: item.assessmentId,
      });
      setConfirmDeleteId(null);
      toast.success("Anbefaling fjernet");
      refetch();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const handleAdd = async () => {
    if (!assessment || !newTitle.trim()) return;
    try {
      await createItem.mutateAsync({
        data: {
          assessmentId: assessment.id,
          title: newTitle.trim(),
          description: newDesc.trim(),
        },
        assessmentId: assessment.id,
      });
      setShowAddForm(false);
      setNewTitle("");
      setNewDesc("");
      toast.success("Anbefaling lagt til");
    } catch {
      toast.error("Kunne ikke legge til");
    }
  };

  return (
    <Card className={isIgnored ? "opacity-50" : ""}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label
              className="flex items-center gap-1.5 cursor-pointer"
              title="Ignorer denne varianten"
            >
              <input
                type="checkbox"
                checked={isIgnored}
                onChange={onToggleIgnored}
                className="h-3 w-3 rounded border-admin-card-border"
              />
            </label>
            <span className="text-sm font-medium text-admin-text-primary">
              {getVariantName(variant.variant)}
            </span>
          </div>
          {assessment && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">
                {[
                  assessment.pipelineVersion,
                  assessment.aiModel,
                  assessment.pipelineVersion === "v2" ? `examples=${assessment.examples}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Badge>
              {showAiInput && (
                <AssessmentAuditButtons
                  assessment={assessment}
                  hasConsultationNotes={false}
                  variantLabel={getVariantName(variant.variant)}
                />
              )}
              {assessment.status === "doctor_approved" && (
                <ExampleTagButtons assessment={assessment} />
              )}
              {assessment.status === "doctor_approved" &&
                assessment.reviewedByName && (
                  <span className="text-xs text-admin-text-subtle">
                    {assessment.reviewedByName}
                  </span>
                )}
              <span
                className={`text-xs ${statusColor[assessment.status] ?? "text-admin-text-subtle"}`}
              >
                {statusLabel[assessment.status] ?? assessment.status}
              </span>
            </div>
          )}
        </div>

        {!assessment ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-sm h-8"
              disabled={isPending}
              onClick={() => setGenerateDialogOpen(true)}
            >
              Generer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 text-sm h-8"
              disabled={isPending}
              onClick={onWriteManually}
            >
              Skriv manuelt
            </Button>
            <AdvancedParamsDialog
              open={generateDialogOpen}
              onOpenChange={setGenerateDialogOpen}
              onGenerate={(params) => onGenerate(params.promptAddition)}
              defaultMetadata={metadata}
              variantName={getVariantName(variant.variant)}
            />
          </div>
        ) : assessment.status === "initiated" ||
          assessment.status === "processing" ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-admin-text-subtle">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Genererer anbefalinger...
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-sm h-8"
              disabled={isPending}
              onClick={onDelete}
            >
              Avbryt
            </Button>
          </div>
        ) : assessment.status === "pending_review" ||
          assessment.status === "doctor_approved" ? (
          <div className="space-y-2">
            {sortedItems.length > 0 ? (
              <div className="space-y-2">
                {sortedItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-admin-card-border rounded p-2 space-y-1"
                  >
                    {editingId === item.id ? (
                      <div className="space-y-1">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Tittel"
                          className="text-sm h-8"
                        />
                        <Textarea
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          className="min-h-[100px] max-h-[40vh] text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 text-xs h-7"
                            disabled={updateItem.isPending}
                            onClick={() => saveEdit(item)}
                          >
                            Lagre
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="flex-1 text-xs h-7"
                            onClick={() => setEditingId(null)}
                          >
                            Avbryt
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-admin-primary">
                            {item.title}
                          </span>
                          {assessment.status === "pending_review" && (
                            <div className="flex items-center gap-2">
                              {confirmDeleteId === item.id ? (
                                <>
                                  <span className="text-xs text-admin-text-subtle">
                                    Slett?
                                  </span>
                                  <button
                                    className="text-xs text-admin-danger-400 hover:text-admin-danger-300"
                                    onClick={() => handleDelete(item)}
                                  >
                                    Ja
                                  </button>
                                  <button
                                    className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                                    onClick={() => setConfirmDeleteId(null)}
                                  >
                                    Nei
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                                    onClick={() => {
                                      setConfirmDeleteId(item.id);
                                      setEditingId(null);
                                    }}
                                  >
                                    Slett
                                  </button>
                                  <button
                                    className="text-xs text-admin-text-subtle hover:text-admin-text-primary"
                                    onClick={() => startEdit(item)}
                                  >
                                    Rediger
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-admin-text-secondary whitespace-pre-wrap">
                          {item.description}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-admin-text-subtle italic">
                Ingen anbefalinger generert
              </p>
            )}

            {assessment.status === "pending_review" && (
              <>
                {showAddForm ? (
                  <div className="border border-admin-card-border rounded p-2 space-y-2">
                    <Input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="Tittel"
                      className="text-sm h-8"
                    />
                    <Textarea
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      placeholder="Beskrivelse..."
                      className="min-h-[80px] text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 text-xs h-7"
                        disabled={!newTitle.trim() || createItem.isPending}
                        onClick={handleAdd}
                      >
                        Legg til
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 text-xs h-7"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewTitle("");
                          setNewDesc("");
                        }}
                      >
                        Avbryt
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full text-sm h-8 text-admin-text-subtle"
                    onClick={() => setShowAddForm(true)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Legg til anbefaling
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 text-sm h-8"
                    disabled={isPending}
                    onClick={onApprove}
                  >
                    Godkjenn
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8 text-admin-text-subtle"
                    onClick={() => setReanalyzeDialogOpen(true)}
                  >
                    Reanalyser
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-sm h-8 text-admin-text-subtle"
                    disabled={isPending}
                    onClick={onDelete}
                  >
                    Slett
                  </Button>
                </div>
                <AdvancedParamsDialog
                  open={reanalyzeDialogOpen}
                  onOpenChange={setReanalyzeDialogOpen}
                  onGenerate={(params) => onReanalyze(params.promptAddition)}
                  defaultMetadata={metadata}
                  variantName={getVariantName(variant.variant)}
                  isReanalysis
                />
              </>
            )}
            {assessment.status === "doctor_approved" && !isCompleted && (
              <Button
                size="sm"
                variant="ghost"
                className="w-full text-sm h-8"
                disabled={isPending}
                onClick={onUndoApprove}
              >
                Angre godkjenning
              </Button>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
